/**
 * Admin UI bilingual strings (en / zh).
 * Used via RegionContext → useRegion().t
 */

export const adminI18n = {
  en: {
    // Tabs
    tabIntro: 'Introduction',
    tabMedia: '1. Dataset',
    tabBuilder: '2. Builder',
    tabShare: '3. Share',
    tabResults: '4. Results',
    tabPractice: 'Practice',
    tabSilicon: 'Silicon',
    siliconPageTitle: 'Silicon pretest',
    siliconDescription: 'Pretest your survey with simulated participants.',
    siliconRunDetails: 'Run details',
    siliconNoRuns: 'No runs yet. Choose personas and questions to start a pretest.',

    // Toolbar
    toggleSidebar: 'Toggle Project Sidebar',
    saveTooltipDirty: 'Save draft changes',
    saveTooltip: 'Save',
    aiTooltip: 'Open the AI Assistant sidebar — models and MCP stay in Settings',
    aiLabel: 'AI',
    toggleAiSidebarOpen: 'Close AI Assistant',
    toggleAiSidebarClosed: 'Open AI Assistant',
    aiSidebarTitle: 'AI Assistant',
    aiSidebarSubtitle: 'Chat to design this project',
    aiSidebarSelectProject: 'Select a project first to send messages.',
    aiSidebarNoProject: 'Select a project to start chatting.',
    aiSidebarNotConnected: 'Not connected — open Settings to add an API key.',
    aiSidebarModelUnavailable: 'Select a configured model before sending. The previous provider may have been removed.',
    aiSidebarSettings: 'Assistant settings',
    aiSidebarSilicon: 'Silicon pretest',
    aiSidebarClear: 'Clear history',
    aiSidebarDownload: 'Download conversation',
    aiSidebarClose: 'Close AI sidebar',
    aiSidebarMore: 'More actions',
    aiSidebarEmptyTitle: 'Ask the Assistant to design this survey',
    aiSidebarEmptyReady: 'AI Assistant is ready. Describe the survey or a change you want.',
    aiSidebarEmptyConnect: 'Open Settings to connect an API key, or use Codex / MCP from Integrations.',
    aiSidebarComposerPlaceholder: "Type a message… (e.g. 'Create a thermal comfort survey')",
    aiSidebarComposerDisabled: 'Select a project and configure a model first.',
    aiSidebarUndo: 'Undo last AI change',
    aiSidebarConnected: 'Connected',
    aiSidebarDisconnected: 'Not connected',
    aiSidebarOpenIntegrations: 'Manage models & MCP',
    aiSidebarModel: 'Model',
    aiSidebarReasoning: 'Reasoning',
    aiSidebarMode: 'Assistant mode',
    aiSidebarModeAgent: 'Agent',
    aiSidebarModeGenerate: 'Generate',
    aiSidebarModeAdjust: 'Adjust',
    aiSidebarModeQuestion: 'Question',
    aiSidebarModeReview: 'Review',
    aiReviewPanelTitle: 'Multi-agent review',
    aiReviewRoles: 'Reviewers',
    aiReviewRole_scientist: 'Scientist',
    aiReviewRole_participant: 'Participant',
    aiReviewRole_planner: 'Planner',
    aiReviewRole_psychologist: 'Psychologist',
    aiReviewRole_analyst: 'Analyst',
    aiReviewRevision: 'Revision',
    aiReviewMethod: 'Method',
    aiReviewMethodLinear: 'Linear individual review',
    aiReviewMethodGroup: 'Group discussion',
    aiReviewMaxRounds: 'Max rounds',
    aiReviewThreshold: 'Accept at',
    aiReviewMaxRoles: 'Reviewer cap',
    aiReviewApplyMode: 'Revisions',
    aiReviewApplyModeReview: 'Review only, do not apply',
    aiReviewApplyModeApply: 'Apply each round',
    aiReviewEstimate: 'Estimate: {calls} model calls, {minTokens}–{maxTokens} tokens',
    aiReviewEstimateCost: '≈ {min}–{max}',
    aiReviewEstimateNoPrice: 'price unknown for this model',
    aiReviewPlaceholder: 'Optional: what should the reviewers focus on? Press Send to start the review.',
    aiReviewRound: 'Round {round}',
    aiReviewAverage: 'Average {rating}/10 (accept at {threshold})',
    aiReviewDecisionAccept: 'Accepted',
    aiReviewDecisionRevise: 'Revise',
    aiReviewDecisionFailed: 'All reviewers failed',
    aiReviewRoleRunning: 'reviewing…',
    aiReviewRoleFailed: 'failed',
    aiReviewRoleCancelled: 'cancelled',
    aiReviewStrengths: 'Strengths',
    aiReviewConcerns: 'Concerns',
    aiReviewSuggestions: 'Suggestions',
    aiReviewSummary: 'Summary',
    aiReviewPlan: 'Revision plan',
    aiReviewOperations: '{count} operation(s)',
    aiReviewApplyRound: 'Apply round {round}',
    aiReviewApplyAll: 'Apply all proposed revisions',
    aiReviewApplied: 'Applied',
    aiReviewRevising: 'revising…',
    aiReviewRevisionInvalid: 'Revision rejected by validation',
    aiReviewConflict: 'Draft changed during the review; not applied',
    aiReviewStatus_running: 'Review in progress',
    aiReviewStatus_accepted: 'Accepted',
    aiReviewStatus_max_rounds: 'Maximum rounds reached',
    aiReviewStatus_failed: 'Review failed',
    aiReviewStatus_revision_failed: 'Revision failed',
    aiReviewStatus_revision_invalid: 'Revision rejected by validation',
    aiReviewStatus_conflict: 'Draft changed during the review',
    aiReviewStatus_apply_failed: 'Applying the revision failed',
    aiReviewStatus_budget_exhausted: 'Token cap reached',
    aiReviewStatus_cancelled: 'Cancelled',
    aiReviewStatusRound: 'Review round {round}: {role}…',
    aiReviewStatusRevising: 'Review round {round}: revising…',
    aiReviewQuestions: 'Questions: {names}',
    aiReviewSettingsEnable: 'Show Review mode in the Assistant composer',
    aiReviewSettingsRoles: 'Default reviewers',
    aiReviewSettingsOverrideNote: 'These are your defaults. The Review options in the composer can still change them for a single run.',
    aiReviewSettingsSaving: 'Saving…',
    aiReviewSettingsSaved: 'Saved to your account',
    aiReviewSettingsError: 'Could not save to your account. Your choice applies in this browser only.',
    aiSidebarSelectModel: 'Select model',
    aiSidebarConfigureModel: 'Configure a model first',
    aiSidebarAssistantName: 'Assistant',
    aiSidebarSend: 'Send message',
    aiSidebarStop: 'Stop current run',
    aiSidebarApprovalPrompt: 'This action requires approval',
    aiSidebarApprovalRisk: 'Risk: {risk}',
    aiSidebarApprovalProject: 'Project: {project}',
    aiSidebarApprovalApprove: 'Approve',
    aiSidebarApprovalDeny: 'Deny',
    aiSidebarSteer: 'Steer',
    aiSidebarSteerPlaceholder: 'Add an instruction to the running Agent…',
    aiSidebarStatusThinking: 'Thinking…',
    aiSidebarStatusQueued: 'Queued…',
    aiSidebarStatusRunning: 'Continuing survey generation…',
    aiSidebarStatusApproval: 'Waiting for your approval…',
    aiSidebarStatusRetrying: 'Retrying model request…',
    aiSidebarStatusCompacting: 'Compacting context and continuing…',
    aiSidebarStatusVerifying: 'Verifying saved draft…',
    aiSidebarStatusUsingTool: 'Using {tool}…',
    aiSidebarStatusStep: 'Working on step {step}…',
    aiSidebarElapsed: '{seconds}s',
    aiSidebarToolRunning: 'running',
    aiSidebarToolDone: 'done',
    aiSidebarToolError: 'failed',
    aiSidebarToolUnknown: 'unknown',
    aiSidebarMemoryHint: 'Memory is project-specific •',
    aiSidebarUnnamedProject: 'Unnamed project',
    aiSidebarCodexTitle: 'Codex / MCP',
    aiSidebarCodexBody: 'Advanced path for Cursor, Claude, and ChatGPT via MCP.',
    aiSettingsTitle: 'Assistant settings',
    aiSettingsModels: 'Models',
    aiSettingsContext: 'Context',
    aiSettingsAdvanced: 'Advanced',
    aiSettingsLocalKeyIntro: 'Connect an OpenAI or OpenRouter API key for this browser.',
    aiSettingsValidate: 'Validate',
    aiSettingsContextTitle: 'Study context',
    aiSettingsContextIntro: 'Optional details that help the Assistant stay aligned with this project.',
    aiSettingsMemory: 'Project memory',
    aiSettingsMemoryHint: 'Keep conversation context and learned preferences within this project.',
    aiSettingsMemoryAutomatic: 'Conversation continuity is handled automatically by the hosted Agent session for this project.',
    aiSettingsResearchTopic: 'Research topic',
    aiSettingsResearchRequirements: 'Research requirements',
    aiSettingsScenario: 'Survey scenario',
    aiSettingsCustomScenario: 'Add a custom scenario',
    aiSettingsAdd: 'Add',
    aiSettingsAdvancedTitle: 'Advanced',
    aiSettingsAdvancedIntro: 'Experimental and maintenance controls. Most studies do not need these.',
    aiSettingsMultiAgent: 'Multi-agent review',
    aiSettingsExperimental: 'Experimental',
    aiSettingsMultiAgentUnavailable: 'Review mode runs a panel of reviewer roles on the current survey, in rounds, and proposes revisions. Set your defaults here.',
    aiSettingsMultiAgentToggle: 'Review generated surveys with multiple specialist prompts',
    aiSettingsIndependentReview: 'Independent reviews',
    aiSettingsGroupReview: 'Group discussion',
    aiSettingsReviewRounds: 'Maximum review rounds',
    aiSettingsEditAgents: 'Edit specialist prompts',
    aiSettingsPrompts: 'System prompts',
    aiSettingsPromptsHint: 'Legacy local assistant only',
    aiSettingsPromptGenerate: 'Generate survey prompt',
    aiSettingsPromptAdjust: 'Adjust survey prompt',
    aiSettingsPromptIntent: 'Intent detection prompt',
    aiSettingsPromptQuestion: 'Question answering prompt',
    aiSettingsReset: 'Reset',
    aiSettingsSave: 'Save',
    aiSettingsConversationData: 'Conversation data',
    aiSettingsConversationDataHint: 'Download this conversation or clear it from the current project.',
    aiClearHistoryTitle: 'Clear conversation',
    aiClearHistoryMessage: 'Clear conversation history for this project?',
    aiClearHistoryConfirm: 'Clear',
    previewSurvey: 'Preview Survey',
    viewLive: 'Survey Link',
    navMainPage: 'Main page',
    moreTools: 'More workspace tools',
    signedInAs: 'Signed in as',
    changeTheme: 'Change theme',
    skillsLibrary: 'Custom interactions',
    adminDashboard: 'Admin Dashboard',
    templatesAndProjects: 'Templates and projects',
    clearEditingState: 'Clear editing state',
    signOut: 'Sign out',
    chooseTheme: 'Choose Theme',
    loadingProjectSystem: 'Loading project system...',
    projectSystemTimeout: 'The project list took too long. The database may be busy — wait a moment and reload.',
    loadingWorkspace: 'Loading workspace…',
    noProjectTitle: 'No Project Selected',
    noProjectBody: 'Create a new project or select an existing one from the sidebar to get started.',
    openProjectSidebar: 'Open Project Sidebar',
    saveStatusSaving: 'Saving…',
    saveStatusError: 'Save failed — click Save to retry',
    saveStatusUnsaved: 'Unsaved changes',
    saveStatusJustNow: 'Auto-saved just now',
    saveStatusSecsAgo: 'Auto-saved {n}s ago',
    saveStatusMinsAgo: 'Auto-saved {n}m ago',
    saveStatusAllSaved: 'All changes saved',

    // Open project header: time up and stored answers
    surveyAgePublished: 'Published {when} · {count}',
    surveyAgeCreated: 'Created {when} · {count}',
    surveyAgeAnswer: '{n} answer',
    surveyAgeAnswers: '{n} answers',
    surveyAgeAnswersUnknown: 'answers unavailable',
    surveyAgeCountLoading: '…',
    surveyAgeWhenJustNow: 'just now',
    surveyAgeWhenMinute: '{n} minute ago',
    surveyAgeWhenMinutes: '{n} minutes ago',
    surveyAgeWhenHour: '{n} hour ago',
    surveyAgeWhenHours: '{n} hours ago',
    surveyAgeWhenDay: '{n} day ago',
    surveyAgeWhenDays: '{n} days ago',

    // Introduction / getting-started guide
    openSurveyBuilder: 'Open Survey Builder',
    guideTitle: 'Get started with SP-Survey',
    guideBody:
      'Start a survey and track your progress.',
    guideStartTour: 'Take the tour',
    guideRestart: 'Restart guide',
    guideRestartHint: 'Shows the project checklist and top-bar tour again.',
    guideHide: 'Hide',
    guideShowQuickstart: 'Show quick start',
    guideShowChecklist: 'Show checklist',
    guideOpenTab: 'Open {tab}',
    guideQuickTitle: 'Your first survey in 5 minutes',
    guideQuickLead: 'The fastest path is to start from a published template and adapt it.',
    guideQuick1: 'Choose a template',
    guideQuick1Action: 'Open projects',
    guideQuick2: 'Import media (optional)',
    guideQuick3: 'Build questions',
    guideQuick4: 'Preview the survey',
    guideQuick4Action: 'Preview',
    guideQuick5: 'Publish and share',
    guideChecklistTitle: 'Project checklist',
    guideChecklistProgress: '{done} of {total} done',
    guideChecklistHint: 'Ticks itself from this project. Preview and sharing are remembered on this browser.',
    guideChecklistAllDone: 'All set: the survey is live and responses are coming in.',
    guideCheckMedia: 'Import media (optional)',
    guideCheckMediaDone: '{n} media files',
    guideCheckMediaTodo: 'Optional for text-only surveys',
    guideCheckQuestions: 'Build questions',
    guideCheckQuestionsDone: '{n} answerable questions',
    guideCheckPreview: 'Preview the survey',
    guideCheckPublished: 'Release the survey',
    guideCheckPublishedDone: 'Version {n} is live',
    guideCheckShared: 'Share the survey link',
    guideCheckResponses: 'View responses',
    guideCheckResponsesDone: '{n} responses',
    guideCheckResponsesUnknown: 'Open Results to check',
    guideStepsTitle: 'Step-by-step workflow',
    guideStatusDone: 'Done',
    guideStatusTodo: 'Not started',
    guideOptional: 'Optional',
    guideBeforeShare: 'Before you share',
    guideWhyLabel: 'What it is for',
    guideDoLabel: 'What you do',
    guideMediaHeadline: 'Add media',
    guideMediaWhy: 'Holds the images, video and audio your questions show. Text-only surveys can skip it.',
    guideMediaDo: 'Upload files or folders, import a Hugging Face dataset, or import a template’s media. Tag folders as set or category so questions can draw from them.',
    guideMediaEmpty: 'No media yet. Upload files, import from Hugging Face, or import a template’s media.',
    guideBuilderHeadline: 'Design pages and questions',
    guideBuilderWhy: 'Defines what participants see: pages, questions, media rules and trial order.',
    guideBuilderDo: 'Add questions (rating, choice, pairwise comparison, ranking, annotation, video and more) or Skills. Preview each question while you edit; changes auto-save to the draft.',
    guideBuilderEmpty: 'No questions yet. Add one in Builder or ask the AI Assistant to draft the survey.',
    guideTryHeadline: 'Try it yourself',
    guideTryWhy: 'Catches unclear wording, broken media and wrong randomisation before real participants see them.',
    guideTryDo: 'Preview in the top bar opens an exact replica of the live survey. Practice lets you answer question by question and inspect the recorded data.',
    guideTryEmpty: 'Not tried yet. Take the full preview once before releasing.',
    guidePreviewAction: 'Open Preview',
    guideSiliconHeadline: 'Pretest with silicon samples',
    guideSiliconWhy: 'AI-simulated respondents give a quick pretest and hypothesis sketch. They never mix into human results.',
    guideSiliconDo: 'Choose a model and sample size, start a run, then review the answers. Runs continue in the background.',
    guideSiliconOff: 'Silicon samples are turned off. Turn them on in Assistant settings to use this step.',
    guideShareHeadline: 'Release and share',
    guideShareWhy: 'Turns your draft into the participant version. The platform hosts the survey link, so there is nothing to deploy.',
    guideShareDo: 'Fix anything the pre-launch check flags, release a version (later releases update the same link), then copy the link or download the QR code.',
    guideShareEmpty: 'Not released yet. Releasing makes the current draft the participant version.',
    guideResultsHeadline: 'Analyze responses',
    guideResultsWhy: 'Turns responses into per-question summaries you can export and report.',
    guideResultsDo: 'Filter responses, read per-question charts, see TrueSkill rankings for comparisons with one tab per category, export CSV/JSON, or ask the AI Assistant to explain a result.',
    guideResultsEmpty: 'No responses yet. Share the link, then come back here.',
    guideAiTitle: 'AI Assistant: help on every step',
    guideAiLead: 'Click AI in the top bar to chat about the current project. Choose a mode in the message box:',
    guideAiAgent: 'Plans and carries out multi-step work: reads the draft, edits it, imports media, and asks before risky actions.',
    guideAiGenerate: 'Drafts a complete survey from your description in one go.',
    guideAiAdjust: 'Makes only the change you ask for and keeps everything else as it is.',
    guideAiQuestion: 'Read-only. Answers questions about the survey or its results without changing anything.',
    guideAiReview: 'Multiple reviewer roles rate and comment on the survey in rounds, then propose revisions you can apply.',
    guideAiOpen: 'Open AI Assistant',
    guideAiSetup: 'Set up models',
    guideAiSetupHint: 'The Assistant uses your own API key (DeepSeek, OpenRouter, OpenAI and others), saved in AI & Integrations.',
    guideAiOff: 'The AI Assistant is turned off. Turn it on in Assistant settings.',
    guideMcpTitle: 'Advanced: Codex, Cursor or Claude Code via MCP',
    guideMcpBody: 'Connect an external agent in AI & Integrations. It edits the same draft with the same safety checks. Example prompt:',
    guideMcpExample: 'Using sp_survey MCP, create a survey about <your topic>.\nAdd question types <question types>.\nMy media is <description> at <folder location>.',
    guideTourStepOf: '{n} of {total}',
    guideTourNext: 'Next',
    guideTourBack: 'Back',
    guideTourSkip: 'Skip tour',
    guideTourDone: 'Done',
    guideTourLabel: 'Workspace tour',
    guideTourProjectsTitle: 'Projects and templates',
    guideTourProjectsBody: 'Open your projects, create a new one, or start from a published template.',
    guideTourTabsTitle: 'Workflow tabs',
    guideTourTabsBody: 'Work left to right: Dataset, Builder, Share, Results. Practice and Silicon help you test.',
    guideTourSaveTitle: 'Auto-save',
    guideTourSaveBody: 'Edits save to your draft automatically. The participant link changes only when you release.',
    guideTourPreviewTitle: 'Preview',
    guideTourPreviewBody: 'Answer the whole survey exactly as participants will.',
    guideTourAiTitle: 'AI Assistant',
    guideTourAiBody: 'Ask the Assistant to build or change the survey from any tab.',
    guideTourLiveTitle: 'View live',
    guideTourLiveBody: 'Opens the participant link in a new tab.',
    guideTourMoreTitle: 'More tools',
    guideTourMoreBody: 'Theme, Skills library, and your account.',
    guideTourRestartTitle: 'Come back any time',
    guideTourRestartBody: 'This Introduction tab keeps the checklist, and Restart guide brings everything back.',

    // Media Dataset
    mediaTitle: 'Media Dataset',
    mediaDescription:
      'Upload and organize survey media.',
    mediaAddSection: '1 · Add media',
    mediaOrganizeSection: '2 · Organize in folders',
    mediaTaggedSection: '3 · Tagged for assignment',
    mediaImportTemplateTitle: 'Import Template / Preview Media',
    mediaImportTemplateHelp:
      'Copy media from a published template or the admin preview media library. Existing files are skipped so imports can resume.',
    mediaUploadTitle: 'Upload Media',
    mediaUploadHelpPrefix: 'Upload into the current folder in Media library',
    mediaUploadLimitsHelp:
      'Each file: max {mb} MB. Image compression is optional; video, audio and animated GIFs retain their original data.',
    mediaAvTooLarge: '"{name}" is too large ({size} MB). Video/audio max is {mb} MB.',
    mediaChooseFiles: 'Choose files',
    mediaSelected: 'selected',
    mediaUploadBtn: 'Upload',
    mediaUploadRoot: '→ root',
    mediaNoFoldersTagged:
      'No folders tagged yet. In Media library, check folders on the left and click Set or Category. Tagged folders will appear here for survey assignment.',
    mediaTaggedSets: 'Tagged sets ({n})',
    mediaTaggedSetsHelp:
      'Folders tagged set. Each folder’s direct files stay together when a question uses “Random fixed sets” with a matching media count.',
    mediaTaggedCategories: 'Tagged categories ({n})',
    mediaTaggedCategoriesHelp:
      'Folders tagged category. Questions can draw random files from each category.',
    mediaCheckingR2: 'Checking R2 for existing images…',
    mediaLastUpload: 'Last upload:',
    hfImportTitle: 'HF Dataset Import',
    hfImportHelp:
      'Batch-import from HuggingFace. Use owner/dataset (rows) or owner/dataset/folder (recursive; nested folders kept). Resume reuses the first import destination to avoid nested duplicates.',
    hfEnable: 'Enable HF import',
    hfToken: 'Token (optional)',
    hfDataset: 'Dataset',
    hfPlaceholder: 'owner/dataset',
    saveConfig: 'Save',
    testConnection: 'Test',
    hfPreload: 'Preload to R2',
    hfRePreload: 'Re-preload to R2',
    noMediaYet: 'No media uploaded yet',
    mediaInR2: 'media file(s) in R2',

    // Media guides
    guideFixedSets: 'Fixed sets',
    guideFixedSetsBody:
      'Tag folders as set. Each tagged folder’s direct files are shown together (set size must match the question’s media count).',
    guideExample: 'Example',
    guideFixedSetsExample:
      '1. Create folders block01, block02, block03\n2. Put 2 images in each folder (same count)\n3. Check those folders → click Set\n4. In Survey Builder, choose media mode Random fixed sets with count = 2\n→ each respondent sees one whole folder (both images together)',
    guideTaggedSets: 'Currently tagged sets: {n}',
    guideCategories: 'Categories',
    guideCategoriesBody:
      'Tag folders as category. In Survey Builder, choose Per category and set Files per category. Each tagged folder contributes that many random file(s) (drawn recursively).',
    guideCategoriesExample:
      '1. Create folders street and park\n2. Put several images in each (subfolders OK)\n3. Check those folders → click Category\n4. In the question: Media Assignment → Per category, Files per category = 2\n→ each respondent sees 2 street + 2 park images (4 total)',
    guideTaggedCategories: 'Currently tagged categories: {n}',
    guideProjectFiles: ' · project files: {n}',

    // Survey Builder (ATF)
    builderTitle: 'Survey Builder',
    builderDescription:
      'Design questions and configure your survey.',
    builderSurveySettings: 'Survey Settings',
    builderNextShare: 'Next: Share Survey →',
    builderBasicInfo: 'Basic Information',
    builderSurveyTitle: 'Survey Title',
    builderSurveyTitleHelp: 'The main title that appears at the top of your survey',
    builderSurveyDescription: 'Survey Description',
    builderSurveyDescriptionHelp: 'A brief description explaining the purpose of your survey',
    builderCompletionMessage: 'Completion Message (optional)',
    builderCompletionMessageHelp: 'Shown to participants after they submit the survey',
    builderResponseQuota: 'Response Quota (optional)',
    builderResponseQuotaHelp: 'Close the survey automatically after this many responses (leave empty for unlimited)',
    builderLogoSettings: 'Logo Settings',
    builderDisplaySettings: 'Display Settings',
    builderSurveyLanguage: 'Survey language',
    builderSurveyLanguageHelp: 'Participant chrome (progress, trials, Next / Complete). Default is English.',
    builderSurveyLanguageEn: 'English',
    builderSurveyLanguageZh: '中文',
    builderThemeCustomization: 'Theme Customization',
    builderPagesQuestions: 'Pages & Questions',
    builderPagesHelp: 'Organize your survey into pages. Drag pages to reorder them.',
    builderAddPage: 'Add New Page',
    builderLayoutStudio: 'Layout Studio',
    builderPreview: 'Preview',
    previewDevice: 'Preview device',
    previewDesktop: 'Desktop',
    previewMobile: 'Mobile',
    previewSizing: 'Sizing: {viewport}',
    previewFormatOnly: 'Layout only. Questions cannot be answered here.',
    previewSurveyTitle: 'Survey title',
    previewSurveyDescription: 'Survey description',
    previewEditSurveyTitle: 'Edit survey title',
    previewEditSurveyDescription: 'Edit survey description',
    previewLogoUrl: 'Logo URL',
    previewLogoHelp: 'Image address shown in the survey header',
    previewContentWidth: 'Content width',
    previewQuestionWidth: 'Question card width',
    previewMediaSize: 'Media height',
    previewQuestionText: 'Question text',
    previewQuestionDescription: 'Question description',
    previewPageTitle: 'Page title',
    previewPageDescription: 'Page description',
    previewMoveUp: 'Move up',
    previewMoveDown: 'Move down',
    previewReorderQuestion: 'Question {index} of {count}: {name}',
    previewReorderPage: 'Page {index} of {count}: {name}',
    previewMoveQuestionUp: 'Move question up',
    previewMoveQuestionDown: 'Move question down',
    previewMovePageUp: 'Move page up',
    previewMovePageDown: 'Move page down',
    previewMediaAssignment: 'Media assignment',

    // Share
    shareTitle: 'Share Your Survey',
    shareDescription:
      'Publish your survey and invite participants.',
    shareYourLink: 'Your Survey Link',
    shareCopied: 'Copied!',
    shareCopyLink: 'Copy Link',
    shareOpenSurvey: 'Open Survey',
    shareQrAlt: 'Survey QR code',
    shareQrDownload: 'Download QR code (PNG)',
    shareQrHint: 'Scan to open this survey. Download a 1024 × 1024 PNG for sharing or printing.',
    shareQrLoading: 'Generating QR code',
    shareQrError: 'Could not generate the QR code.',
    shareQrRetry: 'Try again',
    shareQrLocalHint: 'Local preview only. Use the deployed site to share with participants.',
    shareNoProject: 'No project selected. Please select or create a project first.',
    shareTips: '💡 Sharing Tips',
    shareTip1Primary: 'Share the link directly',
    shareTip1Secondary: 'Send it via email, WeChat, Slack, or any other channel',
    shareTip2Primary: 'Embed in a QR code',
    shareTip2Secondary: 'Download the QR code from Your Survey Link above for invitations, slides or printed materials',
    shareTip3Primary: 'One link for your study',
    shareTip3Secondary: 'With version management enabled, publish a release to update this link. Saving only updates your draft.',
    shareTip4Primary: 'Responses are saved automatically',
    shareTip4Secondary: 'Results go to Supabase in real time and can be viewed in the Results Analysis tab',
    shareAdminLink: '🔒 Admin Panel Link',
    shareAdminHelp: 'Share this link with collaborators who need to edit the survey. They will need to sign in.',
    shareCopyAdmin: 'Copy Admin Link',
    shareCustomLink: 'Custom link',
    shareCustomLinkHint: 'Set a short public address for this survey. The original project link keeps working.',
    shareCustomLinkLabel: 'Custom link name',
    shareCustomLinkSave: 'Save link',
    shareCustomLinkSaving: 'Saving…',
    shareCustomLinkSaved: 'Custom link saved.',
    shareCustomLinkCleared: 'Custom link removed. Participants can still use the project link.',
    shareCustomLinkHelp: '2–40 characters: lowercase letters, digits, and single hyphens. Reserved: admin, api, s, survey, login. Leave blank and save to remove it.',
    shareCustomLinkInvalid: 'Use 2–40 characters: lowercase letters, digits, and single hyphens.',
    shareCustomLinkReserved: 'That name is reserved. Choose another.',
    shareCustomLinkTaken: 'That link is already used by another survey.',
    shareCustomLinkForbidden: 'Only the project owner can change this link.',
    shareCustomLinkUnavailable: 'Custom links are not enabled on this database yet. Paste supabase/project_public_slug.sql in the Supabase SQL editor, then try again.',
    shareCustomLinkFailed: 'Could not save the custom link.',
    shareCustomLinkKeepId: 'The original project link still works:',

    // Results
    resultsScopeHint: "{shown} / {total} submissions in analysis and export.",
    resultsExportHelp: 'The table has one row per submission. The full pack adds per-trial tables, summaries, methods text and the saved analysis report.',
    resultsExportSiteHelp: 'Not analysed by SP-Survey. The ZIP holds the answers table and every record as stored, for your own analysis.',
    resultsBrowserPrefs: "Filters are saved in this browser; they do not modify the survey.",
    resultsResetFilters: "Reset filters",
    resultsFilteredEmpty: "No submissions match these filters. Reset filters to see all responses.",
    resultsPreparingExport: "Preparing export…",
    resultsViewResponse: "View",
    resultsSubmissionDetail: "Submission",
    resultsHistoricalRevision: "Not recorded (historical response)",
    resultsGroupExplanation: "One answer evaluates the entire displayed media group. Each ordered group is counted once.",
    resultsCounts: "{answered} / {total} submissions answered ({rate}%) · {people} participants",
    resultsRecordTrial: "Trial",
    resultsNoMedia: "No media",
    resultsClose: "Close",
    resultsRevision: "Revision",
    resultsQuality: "Quality",
    resultsDetailSession: "Session",
    resultsMixedRevisions: "These responses span multiple questionnaire revisions. Analysis and export use the selected version only to avoid mixing incompatible settings. Switch versions below; historical responses without a revision remain unknown.",
    resultsAllRevisions: "All revisions",
    resultsRowsPerPage: "Rows per page",
    resultsTitle: 'Results Analysis',
    resultsDescription: 'Review, analyze, and export responses.',
    resultsWorkspace: 'Analysis workspace',
    resultsViews: 'Analysis views',
    resultsAttributeFilters: '{n} participant attribute filters',
    resultsDescriptionPrefix: 'Analyze survey responses per question for project:',
    resultsRefresh: 'Refresh responses',
    resultsLoadProgress: 'Loading responses… {loaded} / {total}',
    resultsLoadStarting: 'Reading the first page of responses…',
    resultsLoadProgressUnknown: 'Loading responses… {loaded} so far',
    resultsAnalysisProgress: 'Analyzing responses… {loaded} / {total}',
    resultsExportCsv: 'Export CSV',
    resultsExportAll: 'Export All',
    resultsExportMethods: 'Export Methods',
    resultsViewOverview: 'Overview',
    resultsViewQuestions: 'Questions',
    resultsViewData: 'Data',
    resultsAnalyze: 'Analyze current results',
    resultsExplainQuestion: 'Explain this question',
    resultsFilters: 'Filters',
    resultsExportMenu: 'Export',
    resultsExportReport: 'This analysis report',
    resultsExportSummary: 'Summary statistics',
    resultsExportRaw: 'Raw / trial data',
    resultsExportBundle: 'Full analysis pack (ZIP)',
    resultsExportTable: 'Answers table (CSV)',
    resultsExportSiteAnswers: 'All answers (ZIP)',
    resultsParticipants: 'Participants',
    resultsTrials: 'Answer rounds',
    resultsLatestReport: 'Latest analysis',
    resultsViewReport: 'View report',
    resultsDeleteReport: 'Delete report',
    resultsCancel: 'Cancel',
    resultsIncompleteReport: 'Incomplete analysis',
    resultsReportIncompleteHelp: 'This analysis did not produce a complete report. Run the analysis again.',
    resultsDeleteReportConfirm: 'Delete this saved analysis report? Survey responses will be kept.',
    resultsReportSaveError: 'Could not save the analysis report. Please try again.',
    resultsReportDeleteError: 'Could not delete the report. Please try again.',
    resultsNoReport: 'No analysis report yet. Analyze current results to generate one.',
    resultsReportStaleScope: 'This report used a previous range. Analyze again to update.',
    resultsReportStaleData: 'New responses arrived after this report. Analyze again to update.',
    resultsViewEvidence: 'View evidence',
    resultsDeepPerception: 'Annotation × Perception deep dive',
    resultsOpenPerception: 'Open deep analysis',
    resultsDataSource: 'Data source',
    resultsSourceHuman: 'Human responses',
    resultsSourcePractice: 'Researcher practice only',
    resultsSourceSilicon: 'Silicon run',
    resultsSiliconRun: 'Silicon run ID',
    resultsApplyFilters: 'Apply',
    resultsScopeNow: 'Current range',
    resultsSelectQuestion: 'Select a question',
    resultsQuestionSearch: 'Search questions',
    resultsDateTimezone: 'Dates use {tz}. Records without a valid timestamp are excluded when a date filter is active.',
    resultsQualityIsInfo: 'Quality flags are checks, not deletions.',
    resultsContractFallback: 'Some historical responses have no frozen question contract. Current settings are a fallback.',
    resultsFormalHuman: 'Formal analysis uses human responses unless you choose practice or a Silicon run.',
    resultsConnectedSupabase: 'Connected to Supabase',
    resultsLocalFiles: 'Reading local response files',
    resultsDataQuality: 'Data Quality',
    resultsClean: '{n} clean',
    resultsFlagged: '{n} flagged',
    resultsInAnalysis: '{n} in analysis',
    resultsExcludeFlagged: 'Exclude flagged responses from analysis',
    resultsTotalResponses: 'Total Responses',
    resultsAnswerableQuestions: 'Answerable Questions',
    resultsWithResponses: 'with responses',
    resultsQuestionCoverage: 'Question Coverage',
    resultsCoverageHelp: 'answerable questions with ≥1 response',
    resultsResponseRecords: 'Response records',
    resultsSubmissions: '{n} submissions',
    resultsRecordsHelp: 'Remove individual submissions from this project. Deletion is permanent.',
    resultsRecordsViewHelp: 'View answers, trial details and quality flags for each submission.',
    resultsEmpty: 'No responses collected yet. Share your survey link and come back here to view results.',
    resultsSkillCustomHelp:
      'Custom skill results reuse native charts by declared type: annotation overlays (points/path/polygon/bbox), media ranking (number/boolean with imageUrl), or text/ranking/allocation charts.',
    resultsSkillByMode: 'View by task mode',
    resultsSkillByModeHelp:
      'This skill stores multiple tasks in one question, distinguished by mode (e.g. cue ranking / route trace).',
    resultsSkillFieldStats: 'Field statistics',
    resultsSkillResponseList: 'Responses',
    resultsSkillExampleFirst: 'Example (first response)',
    resultsSkillReadableSummary: 'Readable summary',
    resultsSkillHideAllRaw: 'Hide all raw responses',
    resultsSkillShowAllRaw: 'View all raw responses',
    resultsSkillHideRawJson: 'Hide raw JSON',
    resultsSkillShowRawJson: 'Show raw JSON',
    resultsSkillReadableHint: 'One readable summary per person; expand raw JSON when needed.',
    resultsSkillRawHint: 'Answer fields only — shown media is listed separately per response.',
    resultsSkillResponseN: 'Response {n}',
    resultsSkillStimulusMedia: 'Stimulus media:',
    resultsSkillShowLess: 'Show less',
    resultsSkillShowAllN: 'Show all {n}',
    resultsSkillNoResponses: 'No responses yet.',
    resultsSkillHideRaw: 'Hide raw responses',
    resultsSkillViewRaw: 'View raw responses',
    skillAnswerSubmittedTitle: 'Submitted answer',
    skillAnswerYourSubmittedTitle: 'Your submitted answer',
    skillAnswerRecorded: 'Recorded',
    skillAnswerEmpty: '(no answer)',
    skillAnswerNotRecorded:
      'No answer was recorded for this question (submit button did not save, or it was skipped).',

    // Practice
    practiceTitle: 'Researcher Practice',
    practiceDescription:
      'Test questions before collecting responses.',
    practiceQuestions: 'Questions',
    practiceStartSession: 'Start session',
    practiceSessionLocked: 'Session locked — answer the queue, or stop the session to free-pick again.',
    practiceFreePick: 'Choose a question to practice, or start a session.',
    practiceChooseQuestion: 'Choose a question to practice.',
    practiceTotal: 'Practice total: {n}',
    practiceNewRound: 'New round',
    practiceEndSession: 'End session',
    practiceEditSettings: 'Question settings',
    practiceSettingsSaved: 'Question settings updated. Save the project to persist.',
    practiceSettingsUnavailable: 'Cannot edit question settings without a survey configuration.',

    // Sidebar
    sidebarProjects: 'Projects',
    sidebarTemplates: 'Project Templates',
    sidebarSearchTemplates: 'Search templates...',
    sidebarClearSearch: 'Clear search',
    sidebarTemplateFilters: 'Filter and sort templates',
    sidebarCategory: 'Category',
    sidebarAllCategories: 'All categories',
    sidebarSort: 'Sort by',
    sidebarNewest: 'Newest',
    sidebarOldest: 'Oldest',
    sidebarNameAZ: 'Name A–Z',
    sidebarNameZA: 'Name Z–A',
    sidebarTemplateCount: '{shown} of {total} templates',
    sidebarShowMore: 'Show more',
    sidebarShowLess: 'Show less',
    sidebarMyProjects: 'My Projects ({n})',
    sidebarImportProject: 'Import project (.json, or Qualtrics .qsf)',
    sidebarCreateProject: 'Create New Project',
    sidebarNoProjects: 'No projects yet. Create your first project!',
    sidebarNoTemplates: 'No templates found. Create one from a project.',
    sidebarNoTemplateMatch: 'No templates match your search.',
    sidebarProjectName: 'Project Name',
    sidebarDescriptionOptional: 'Description (Optional)',
    sidebarCancel: 'Cancel',
    sidebarCreate: 'Create',
    sidebarEditProject: 'Edit Project',
    sidebarDuplicate: 'Duplicate',
    sidebarExportProject: 'Export Project',
    sidebarSaveAsTemplate: 'Save as Template',
    sidebarPublishMain: 'Publish to Main Page',
    sidebarDelete: 'Delete',
    sidebarShared: 'Shared',
    collaboratorTitle: 'Collaborators',
    collaboratorEmail: 'Email of an existing account',
    collaboratorEmailInvalid: 'Enter a valid email address.',
    collaboratorAdd: 'Add collaborator',
    collaboratorInviteTooltip: 'Invite a collaborator by email',
    collaboratorRemove: 'Remove {email}',
    collaboratorAdded: 'Added {email}',
    collaboratorRemoved: 'Removed collaborator',
    collaboratorNoAccount: 'No account uses that email yet. They need to sign up first.',
    collaboratorOwnerOnly: 'Only the project owner can change collaborators.',
    collaboratorAlreadyOwner: 'That person already owns this project.',
    collaboratorMigrationMissing: 'Run supabase/project_collaborators.sql once in the Supabase SQL editor, then try again.',
    collaboratorSaveFailed: 'Could not update collaborators',
    presenceTitle: 'Also here',
    presenceOnlyYou: 'Only you have this project open',
    presencePerson: '{name} has this project open',

    // Integrations
    integBackAdmin: 'Back to Admin',
    integTitle: 'AI & Integrations',
    integBody:
      'Use the in-browser Assistant with your own API key (DeepSeek recommended). MCP for Codex / Claude / Cursor is an advanced option.',
    integConnectTitle: 'Advanced — Connect ChatGPT (Codex)',
    integAdvanced: 'Advanced',
    integAssistantFirstBody:
      'Enter your API keys to use models from the following providers. Keys are write-only after save, same as DeepSeek Harness.',
    integAssistantModel: 'Assistant model',
    integSiliconModel: 'Silicon VLM',
    integRecommended: 'Recommended',
    integConnected: 'Connected',
    integConnectBody:
      'Use ChatGPT (Codex) to create and edit surveys. Saves update the share link immediately. No OpenAI API key is needed on this website.',
    integMcpEndpoint: 'MCP endpoint (same URL for Codex / Claude Code / Cursor):',
    integLocalMcpHint: 'Local server MCP (dev only)',
    integStep1Title: 'Set ChatGPT (Codex) permission to "Approve for me"',
    integStep1Body:
      'In the ChatGPT (Codex) Work composer, click the permission control (bottom-left of the input) and choose Approve for me. This lets ChatGPT (Codex) edit ~/.codex/config.toml, run codex mcp login, open your browser, and save credentials.',
    integStep2Title: 'Paste this into ChatGPT (Codex)',
    integStep2Body: 'Copy into a new ChatGPT (Codex) chat:',
    integStep3Title: 'Start a new chat, then design a survey',
    integStep3Body: 'Open a new ChatGPT (Codex) chat, then paste:',
    integClaudeTitle: '2. Connect Claude Code',
    integClaudeBody:
      'Use Claude Code in the terminal with the same sp_survey MCP. Saves update the share link immediately. Approve OAuth once in your browser.',
    integClaudeStep1Title: 'Add the MCP server',
    integClaudeStep1Body:
      'In a terminal (or paste the prompt below into Claude Code), add the remote HTTP MCP. Prefer user scope so it works across projects:',
    integOrPastePrompt: 'Or paste this setup prompt into the agent:',
    integClaudeStep2Title: 'Authenticate in Claude Code',
    integClaudeStep2Body:
      'Run /mcp, select sp_survey, then Authenticate. Open the authorize URL in your system browser and Approve on this site. Restart Claude Code if tools do not appear.',
    integClaudeStep3Title: 'Design a survey',
    integClaudeStep3Body: 'In a new Claude Code session, paste:',
    integCursorTitle: '3. Connect Cursor',
    integCursorBody:
      'Use Cursor Agent with the same sp_survey MCP. Add the server JSON, then Connect / Authenticate under Settings → Tools & MCP.',
    integCursorStep1Title: 'Add ~/.cursor/mcp.json (or project .cursor/mcp.json)',
    integCursorStep1Body:
      'Merge into existing mcpServers — do not delete other servers. Cursor uses url only (no type field):',
    integCursorStep2Title: 'Authenticate in Cursor',
    integCursorStep2Body:
      'Open Cursor Settings → Tools & MCP. Find sp_survey and click Connect / Authenticate. Approve in the browser on this site. Reload the window if tools stay grey.',
    integCursorStep3Title: 'Design a survey',
    integCursorStep3Body: 'In a new Cursor Agent chat, paste:',
    integAccounts: 'Connected MCP accounts',
    integNoneYet: 'None yet. Complete a Codex, Claude Code, or Cursor connection above.',
    integLastUsed: 'Last used:',
    integNever: 'Never',
    integDisconnect: 'Disconnect',
    integAssistantTitle: '1. In-browser AI Assistant',
    integReady: 'Ready',
    integAssistantBody:
      'Prefer to stay in the browser? Add your OpenAI or OpenRouter API key, then use the assistant inside Survey Builder.',
    integGetKey: 'Get a key from DeepSeek, OpenRouter, or OpenAI.',
    integApiKeyLabel: 'API key',
    integSaveKey: 'Save key',
    integRemoveKey: 'Remove key',
    integAfterSave: 'After saving, return to Admin, open a project, and expand the AI Assistant panel.',
    integCopy: 'Copy',
    modelsTitle: 'Models',
    modelsIntro: 'Enter your API keys to use models from the following providers.',
    modelsEdit: 'Edit',
    modelsAdd: 'Add provider',
    modelsProvider: 'Provider',
    modelsCancel: 'Cancel',
    modelsApply: 'Apply',
    modelsApplying: 'Applying…',
    modelsSavedProvider: 'Saved {provider}.',
    modelsCredentialConfigured: 'API key configured',
    modelsCredentialMissing: 'API key missing',
    modelsLoadFailed: 'Loading the provider directory failed',
    modelsReload: 'Try again',
    modelsKeyInput: 'API key',
    modelsKeyPlaceholder: 'Enter your API key',
    modelsKeyStored: 'Configured — enter a new value to replace',
    modelsCustomized: 'Customized settings',
    modelsBaseUrl: 'Base URL',
    modelsBaseUrlDefault: 'Provider default',
    modelsModels: 'Models',
    modelsSearchModels: 'Search models',
    modelsSearchModelsHint: 'Search by model name or ID',
    modelsNoMatchingModels: 'No matching models.',
    modelsAddModel: 'Add model',
    modelsRemoveModel: 'Delete model',
    modelsModelId: 'Model ID',
    modelsModelName: 'Display name',
    modelsModelNamePlaceholder: 'Uses the model ID when empty',
    modelsFetchModels: 'Fetch available models',
    modelsFetching: 'Asking the provider…',
    modelsFetchNeedsBaseUrl: 'Enter the base URL first, then fetch.',
    modelsFetchEmpty: 'The provider listed no models. Add them by hand.',
    modelsFetchTitle: 'Choose models to add',
    modelsFetchDescription: 'These are the models this provider has available. Choose the ones to add.',
    modelsFetchSelectAll: 'Select all',
    modelsFetchDeselectAll: 'Deselect all',
    modelsFetchAdopt: 'Add selected',
    modelsCustomAdd: 'Add a custom provider',
    modelsCustomTitle: 'Custom provider',
    modelsCustomTag: 'Custom',
    modelsCustomRoute: 'Provider ID',
    modelsCustomRouteHint: 'Lowercase identifier, starting with a letter, that uniquely names this provider in requests and as its credential name.',
    modelsCustomRouteInvalid: 'Start with a lowercase letter; then lowercase letters, digits, and dashes.',
    modelsCustomRouteTaken: 'A provider already uses this ID.',
    modelsCustomDisplayName: 'Display name',
    modelsCustomApi: 'API protocol',
    modelsCustomNeedsBaseUrl: 'A custom provider needs a base URL.',
    modelsCustomNeedsModels: 'A custom provider needs at least one model.',
    modelsCustomBaseUrlPlaceholder: 'https://gateway.example/v1',
    modelsCreate: 'Create provider',
    modelsCreating: 'Creating…',
    modelsOnboardingTitle: 'Add an API key to get started',
    modelsKeyRequired: 'Enter an API key to continue.',
    modelsSelectModel: 'Select model',
    modelsSelectVision: 'Select a vision model',
    modelsDefaults: 'Default models',
    modelsDeleteDescriptionWithCredential: 'Deleting {provider} removes its configuration and stored API key.',
    modelsAuthUnsupported: 'Native authentication is not configurable in this release',
    modelsGroupRecommended: 'Recommended',
    modelsGroupConfigured: 'Configured',
    modelsGroupCatalog: 'Catalog',
    modelsGroupCustom: 'Custom',
    modelsGroupUnsupported: 'Native authentication (not configurable)',
    modelsContextWindow: 'Context window',
    modelsMaxOutput: 'Max output',
    modelsInputText: 'Text',
    modelsInputImage: 'Image',
    modelsReasoning: 'Reasoning',
    modelsReasoningOff: 'Off',
    modelsDefaultEffort: 'Default effort',
    modelsRetry: 'Max retries',
    modelsSettingsConflict: 'Someone else changed these settings. Close and reopen to edit the current values.',
    modelsDiscovery401: 'Fetching available models returned 401.',
    modelsDiscoveryUnsupported: 'This protocol does not support model discovery.',
    modelsDiscoveryTooLarge: 'Provider response exceeded 4 MiB.',
    modelsDiscoveryMalformed: 'Provider returned malformed JSON.',
    modelsDiscoveryNetwork: 'Could not reach the provider. Check the URL and try again.',
    siliconOverrideModel: 'Vision model for this run',
    siliconEffort: 'Reasoning effort',
    siliconDisclaimer: 'Silicon samples are for pretest and hypothesis sketch. They never mix into human quota or default Results, and they do not prove that a design is scientifically valid.',
    siliconPersonas: 'Personas',
    siliconQuestions: 'Questions',
    siliconRunSettings: 'Run settings',
    siliconQuestionNumber: 'Question {n}',
    siliconSearchQuestions: 'Search by number or question',
    siliconSelectAll: 'Select all supported',
    siliconClearSelection: 'Clear selection',
    siliconSelectedCount: '{n} selected',
    siliconNoMatchingQuestions: 'No matching questions.',
    siliconRemoveFailed: 'Could not remove persona.',

    siliconName: 'Name',
    siliconCity: 'City / country',
    siliconNotes: 'Scenario notes',
    siliconAddPersona: 'Add persona',
    siliconRemove: 'Remove',
    siliconRun: 'Run',
    siliconRepeats: 'Repeats',
    siliconBudget: 'Token budget',
    siliconDraftSnapshot: 'Each run snapshots the current saved draft and its project media.',
    siliconBudgetUsage: '{used} / {budget} tokens',
    siliconStart: 'Run silicon pretest',
    siliconNeedVlm: 'Uses your silicon VLM (must accept images). Set it under AI & Integrations.',
    siliconRuns: 'Runs',
    siliconCancel: 'Cancel',
    siliconCompare: 'Compare',
    siliconCount: 'Synthetic responses',
    siliconAnswers: 'answers',
    siliconPickPersona: 'Select at least one persona.',
    siliconResume: 'Continue unfinished units',
    siliconRetryFailed: 'Retry failed units only',
    siliconResumeHint: 'Keeps saved answers and only processes pending units.',
    siliconRetryHint: 'Resets failed or unknown units on this run. Does not start a new batch.',
    siliconReuseHint: 'Creates a new run with the same settings. Saved answers stay on the old run.',
    siliconUnverifiedQs: 'Listed but not preview-accepted',
    siliconErrorLease: 'Silicon background leases are not applied on this database.',
    draftSaveRpcMissing: 'Draft save is not ready on this database. Apply save_project_draft_revision_id.sql.',
    assistantRunSchemaMissing: 'Assistant run columns are not applied on this database.',
    siliconExport: 'Export JSON',
    siliconExportCsv: 'Export CSV',
    siliconDraftVersion: 'Draft version',
    siliconModelLabel: 'Model',
    siliconSelectedPersonas: 'Selected personas',
    siliconSupportedQs: 'Silicon can answer',
    siliconUnsupportedQs: 'Silicon will skip',
    siliconNoAnswerable: 'No answerable questions in the saved draft.',
    siliconSelectQuestions: 'Select at least one supported question before starting.',
    siliconSnapshotPages: 'snapshot pages',
    siliconSnapshotMedia: 'snapshot media',
    siliconCannotStart: 'No supported questions selected. Unsupported items are listed and will not be treated as a full-survey pass.',
    siliconPartialPretest: 'This is a partial pretest of supported questions only. Untested items are listed and a local pass is not a full-survey pass.',
    siliconOpenPersona: 'Open persona answers',
    siliconValidRate: 'Valid rate',
    siliconFailureReason: 'Failure',
    siliconShownMedia: 'Shown media',
    siliconStatusAllFailed: 'Finished — no valid answers',
    siliconStatusPartialValid: 'Finished — some answers invalid or skipped',
    siliconProgressCounts: 'Processed {done}/{total} trials · valid {answered} · skipped {skipped} · failed {errors} · remaining {remaining}',
    siliconStatusAllComplete: 'Finished with valid answers',
    aiChangeCardTitle: 'What changed',
    aiChangeVerified: 'Saved and verified',
    aiChangeSaved: 'Saved',
    aiChangeUnsaved: 'Not saved',
    aiChangeSaveFailed: 'Save failed',
    aiChangeVerifyIncomplete: 'Verification incomplete',
    aiChangeNoDiff: 'No field-level changes to show',
    aiWriteConflict: 'The editor has unsaved changes. Save before executing this request, or return to the editor.',
    aiSaveThenRun: 'Save then execute',
    aiReturnToEditor: 'Return to settings',
    aiQueueTitle: 'Queued tasks',
    aiQueueAfter: 'After this task',
    aiQueueNow: 'Steer current',
    aiQueueCancel: 'Cancel',
    siliconStatusPartial: 'Partial — budget stopped the run',
    siliconStatusBudgetFailed: 'Stopped — token budget exhausted',
    siliconStatusFailed: 'Failed',
    siliconStatusCancelled: 'Cancelled',
    siliconStatusRunning: 'Running',
    siliconStatusLoading: 'Loading counts…',
    siliconTasksTitle: 'Silicon tasks',
    siliconTasksTab: 'Tasks · {count}',
    siliconTasksBadge: 'Tasks · {count} running',
    collectingSurveysBadge: 'Surveys · {count} collecting',
    collectingSurveysTitle: 'Your published surveys that are still open for responses. An explicit collecting flag is used when the project has one; otherwise a published release that is not marked closed counts. Drafts and other people’s surveys are not counted.',
    siliconTasksActive: '{count} Silicon runs in progress',
    siliconTasksRecent: 'Finished',
    siliconTasksEmpty: 'No Silicon runs yet.',
    siliconTasksEmptyActive: 'No Silicon runs in progress.',
    siliconTasksEmptyEnded: 'No finished Silicon runs yet.',
    siliconTasksInProgress: 'In progress ({count})',
    siliconTasksEnded: 'Finished ({count})',
    siliconTasksBack: 'Back',
    siliconProcessTitle: 'Run process',
    siliconProcessSamples: 'Processed {done}/{total} trials',
    siliconProcessUnits: 'Processed {processed}/{total} trials: valid {valid}, skipped {skipped}, failed {failed}',
    siliconProcessCurrent: 'Current: {persona} · response {repeat} · {question} · trial {trial}/{trials}',
    siliconProcessWaiting: 'Waiting for the model · {seconds}s',
    siliconProcessTimeline: 'Technical details',
    siliconConfigSummary: '{personas} personas · {envelopes} simulated responses · {questions} questions, {trials} trials',
    siliconProcessedTrials: 'Processed {done}/{total} trials',
    siliconEndedNoAnswers: 'Finished, no valid answers',
    siliconTrialBreakdown: 'Processed {done}/{total} trials: valid {valid}, skipped {skipped}',
    siliconNoImagesReason: 'Reason: no usable images were resolved',
    siliconReuseSettings: 'Reuse settings and run again',
    siliconMediaPreviewLibrary: 'This run uses the platform preview library · {count} images available',
    siliconMediaProject: 'This run uses project media · {count} images available',
    siliconMediaNone: 'No usable media source for this run',
    siliconEnvelopeLabel: '{persona} · response {repeat}',
    siliconQuestionCard: 'Question {n} · {title}',
    siliconTrialProgress: 'Processed {done}/{total} trials · now on trial {current}',
    siliconWaitingStart: 'Waiting to start',
    siliconTechDetails: 'Technical details',
    siliconEndedAt: 'Ended {time}',
    siliconDuration: 'Ran {duration}',
    siliconDurationSeconds: '{seconds}s',
    siliconDurationMinutes: '{minutes}m {seconds}s',
    siliconStatusQueued: 'Queued',
    siliconStatusThrottled: 'Waiting for rate limit',
    siliconStatusStopping: 'Stopping',
    siliconErrorNoMedia: 'No usable media source. The draft has no project media and the preview library is empty.',
    siliconErrorFolderEmpty: 'The selected folders have no matching images.',
    siliconErrorImageUnreadable: 'An image URL could not be read.',
    siliconErrorModel: 'The model request failed.',
    siliconViewProcess: 'View process',
    siliconViewAnswers: 'View saved answers',
    siliconOpenProject: 'Open this project',
    siliconStopping: 'Stopping…',
    siliconConnectionLost: 'Connection interrupted. The task is still on the server — this is not a task failure.',
    siliconTaskFinished: 'Silicon run for {project} is {status}.',
    siliconTaskCounts: '{processed}/{total} trials · {valid} valid · {failed} failed',
    siliconRationale: 'Answer basis',
    siliconEventStart: 'Started this question',
    siliconEventMedia: 'Prepared images for this trial',
    siliconEventRequest: 'Requesting the model',
    siliconEventResponse: 'Received an answer',
    siliconEventValid: 'Answer passed validation',
    siliconEventInvalid: 'Answer failed validation',
    siliconEventSaved: 'Answer saved',
    siliconEventSkip: 'Skipped',
    siliconEventRetry: 'Retrying',
    siliconEventThrottle: 'Queued / rate limited',
    siliconEventStopping: 'Stopping',
    siliconEventComplete: 'Run finished',
    siliconEventFailed: 'Run failed',
    siliconStageQueued: 'Queued',
    siliconScienceNote: 'These counts are a synthetic pretest, not evidence that the instrument is scientifically valid.',
    featureAssistant: 'In-browser Assistant',
    featureAssistantHint: 'Independent of Silicon. Turn off to hide the Assistant sidebar.',
    featureSilicon: 'Silicon pretest (experimental)',
    featureSiliconHint: 'Independent of the Assistant. Keep off until the pretest contract is ready for a study.',
    qualityChecks: 'Run design checks',
    qualityChecksEmpty: 'No on-demand design issues found.',
    qualityCheckResearch: 'Research design',
    qualityCheckFlow: 'Participant flow',
    qualityCheckExport: 'Analysis / export',
    resultsSiliconHint: 'Human results only by default. Open the Silicon Samples tab to compare synthetic VLM answers.',

    // Public header / footer
    navDocs: 'SP-Wiki',
    navPaperLibrary: 'Paper library',
    navNews: 'News',
    navForum: 'SP-Forum',
    navRequestTemplate: 'Request template',
    navRequestDesign: 'Request design',
    navTeam: 'Team',
    navLiveSurveys: 'Live surveys',
    navSpBench: 'SP-Bench',
    navResearcherLogin: 'Researcher login',
    navOpenWorkspace: 'Open workspace',
    footerDevelopedBy: 'Developed by',
    footerCite: 'Cite & publications',

    newsTitle: 'News',
    newsSubtitle: 'Product updates, releases, and research notes from SP-Survey.',
    newsEmpty: 'No posts yet. Check back soon.',
    newsBackToList: 'All news',
    newsNotFound: 'This post was not found or is no longer published.',
    newsNoBody: 'No content.',
    newsUnavailable: 'News is not available yet.',

    benchNotOpenTitle: 'SP-Bench',
    benchNotOpenBody: 'This benchmark is not open to the public yet.',
    benchMethod: 'Method',
    benchDataset: 'Dataset',
    benchSamples: 'samples',
    benchModels: 'models',
    benchOverall: 'Overall',
    benchObjective: 'Objective',
    benchSubjective: 'Subjective',
    benchCognition: 'Cognition',
    benchMethodTab: 'Methodology',
    benchLandingFallback: 'Benchmarking subjective–objective spatial perception and cognition in urban environments.',
    benchNoResultsYet: 'Leaderboard coming soon',
    benchViewLeaderboard: 'View leaderboard',

    // Landing page
    feedbackButton: 'Suggestions',
    feedbackTitle: 'Send us a suggestion',
    feedbackAnonymousHint: 'No sign-in needed. If you sign in first, we can see which account it came from.',
    feedbackSignedInAs: 'Sending as {email}.',
    feedbackCategory: 'Type',
    feedbackCatSuggestion: 'Suggestion',
    feedbackCatFeature: 'Feature request',
    feedbackCatBug: 'Problem / bug',
    feedbackCatOther: 'Other',
    feedbackMessage: 'Your suggestion',
    feedbackMessagePlaceholder: 'What would make SP-Survey better for your research?',
    feedbackContactTitle: 'Contact (optional, so we can follow up)',
    feedbackContactName: 'Name',
    feedbackContactEmail: 'Email',
    feedbackContactWechat: 'WeChat ID',
    feedbackContactOther: 'Other contact',
    feedbackContactOtherPlaceholder: 'Phone, X, LinkedIn…',
    feedbackSubmit: 'Send',
    feedbackSending: 'Sending…',
    feedbackThanks: 'Thank you! Your suggestion has been sent to the SP-Survey team.',
    feedbackSendAnother: 'Send another',
    feedbackRateLimited: 'You have sent several suggestions just now. Please wait a moment and try again.',
    feedbackUnavailable: 'Suggestions are not available yet. Please try again later.',
    feedbackFailed: 'Could not send. Please check your connection and try again.',
    landGrowthTitle: 'Platform growth',
    landGrowthSubtitle: 'Live totals from the platform, with the last 12 months of growth. Only platform admins can see this.',
    landGrowthUnavailable: 'Growth stats are unavailable. Run supabase/site_growth_stats.sql in Supabase, and make sure you are a platform admin.',
    landGrowthUsers: 'Researchers',
    landGrowthProjects: 'Survey projects',
    landGrowthResponses: 'Responses collected',
    landGrowthVisits: 'Visits (30 days)',
    landGrowthTotalHint: 'All time',
    landGrowthVisitorsHint: '{n} unique visitors (counted per day)',
    landLiveBannerOne: '{n} live survey is online now — take it as a participant.',
    landLiveBannerMany: '{n} live surveys are online now — take them as a participant.',
    landBrowseLive: 'Browse Live Surveys',
    landHeroTitle: 'Streetscape Perception Survey',
    landHeroLine1: 'A research-grade platform for conducting visual perception surveys on urban streetscapes.',
    landHeroLine2: 'Build, share, and analyze streetscape perception studies — no coding required.',
    landStartFree: 'Start for Free →',
    landReadPaper: 'Read Paper',
    landFeaturesTitle: 'Everything you need for perception research',
    landFeatUploadTitle: 'Upload Images',
    landFeatUploadDesc: 'Upload street-view images directly to cloud storage. Auto-compressed to keep surveys fast.',
    landFeatAiTitle: 'AI Survey Builder',
    landFeatAiDesc: 'Describe your research goals and let ChatGPT generate a complete survey with validated question types.',
    landFeatShareTitle: 'Instant Sharing',
    landFeatShareDesc: 'Get a shareable link immediately — no server setup, no deployment, no configuration.',
    landFeatResultsTitle: 'Results Analysis',
    landFeatResultsDesc: 'View responses per question with image–response pairing and export to CSV.',
    landTemplatesTitle: 'Template Library',
    landSchemeTitle: 'Surveys designed by AI with SP-Scheme',
    landSchemeSubtitle: 'Each page was designed by a language model from the SP-Scheme spec, then uploaded as the whole survey. Participants get exactly this page; the platform records and analyses the answers. Preview one, or start your own from it.',
    landTemplatesSubtitle: 'Start from peer-reviewed survey designs used in published research',
    landRequestTemplate: 'Request a Template for Your Paper',
    landRequestDesign: 'Request Survey Design Help',
    landSearchTemplates: 'Search templates by name, author, or topic...',
    landCatAcademic: 'Academic Research',
    landCatUrban: 'Urban Theory',
    landCatAi: 'AI Generated',
    landSearchCategoryHint: 'Search to explore all {n} templates in this category.',
    landNoTemplates: 'No templates found for "{q}"',
    landCtaTitle: 'Ready to start your survey?',
    landCtaBody: 'Create an account and launch your first survey in under 10 minutes.',
    landGetStarted: 'Get Started Free',
    landPreview: 'Preview',
    landUse: 'Use',
    landPaper: 'Paper',
    landDataset: 'Dataset',
    landChipAi: 'AI',
    landChipUrban: 'Urban',
    landChipAcademic: 'Academic',
    landPreviewTitle: 'Preview — {name}',
    landReadOnly: '(read-only)',
    landClose: 'Close',

    trialDoNTimes: 'You will do this {n} times. This is {current} of {n}.',
    trialProgressCount: 'Completed {done} / {n}. {left} remaining.',
    trialAutoAdvance: 'After you answer, the next round starts automatically.',
    trialAutoAdvanceAllocation: 'The next round starts when every option is set or the points are all used. Use Next round to move on with points left.',
    trialUseNext: 'Use Next round after you answer.',
    trialLastRound: 'Last round — then continue the survey.',
    trialAllDone: 'All {n} rounds answered.',
    trialContinue: 'Continue to next question',
    trialFinishSurvey: 'Finish survey',
    trialBack: 'Back',
    trialNextRound: 'Next round',
    trialPageNextBlocked: 'Please complete all rounds before continuing.',
    progressRound: 'Round {current} / {n}',
    trialLabel: 'Round {n}',
    trialReviewAnswers: 'Review answers',
    progressPage: 'Page {current} / {n}',
    progressQuestion: 'Question {current} / {n}',
    progressQuestionLabel: 'Question {n}',
    progressTrialCount: '{done}/{n} rounds',
    progressNotReached: 'Not reached yet',
    progressJump: '{label} — click to jump',
    progressPreviousGroup: 'Previous question group',
    progressNextGroup: 'Next question group',
    participantSaving: 'Saving your responses…',
    participantThanks: 'Thank you!',
    participantCompleted: 'Thank you for completing the survey!',
    participantRoundsCompleted: 'All {n} annotation rounds completed!',
    participantCompletionCode: 'Completion code:',
    participantIdLabel: 'Your participant ID:',
    participantSavedLocally: 'Responses saved locally.',
    participantSaved: 'Responses saved successfully.',
    participantSaveError: 'We could not save your responses due to a network or server issue. Your answers are preserved — please try again.',
    participantRetrySubmission: 'Retry submission',
    participantResumeTitle: 'Resume previous session?',
    participantResumeBody: 'We found an unfinished survey from {date}. Would you like to continue where you left off?',
    participantPreviousVisit: 'a previous visit',
    participantStartFresh: 'Start fresh',
    participantResume: 'Resume',

    // Login
    loginBrand: 'Streetscape Perception Survey',
    loginSubtitle: 'Researcher login',
    loginSignIn: 'Sign In',
    loginCreateAccount: 'Create Account',
    loginEmail: 'Email',
    loginPassword: 'Password',
    loginPasswordHelp: 'Minimum 6 characters',
    loginSigningIn: 'Signing in...',
    loginCreating: 'Creating account...',
    loginRegisterSuccess: 'Registration successful! Please check your email to confirm your account, then log in.',
    loginErrorGeneric: 'An error occurred',
    loginBackHome: '← Back to home',
    loginForgot: 'Forgot password?',
    loginResend: 'Resend confirmation email',
    loginEnterEmailFirst: 'Enter your email above first.',
    loginResendSent: 'Confirmation email sent again. Check your inbox and spam folder.',
    loginForgotTitle: 'Reset your password',
    loginForgotHelp: 'Enter the email you registered with. We will send a link to set a new password.',
    loginSendResetLink: 'Send reset link',
    loginResetSent: 'If an account exists for this email, a reset link is on its way. Check your inbox and spam folder.',
    loginResetLinkInvalid: 'This reset link is invalid or has expired. Request a new one below.',
    loginBackToSignIn: 'Back to sign in',
    loginNewPasswordTitle: 'Set a new password',
    loginNewPassword: 'New password',
    loginSaveNewPassword: 'Save new password',
    notFoundTitle: 'Page not found',
    notFoundBody: 'This address does not exist on SP-Survey. If someone sent you a survey link, check that it was copied in full.',
    notFoundHome: 'Go to the homepage',

    // Live surveys
    liveTitle: 'Live Surveys',
    liveBody: 'Community studies currently featured by the platform. Participant links follow the researcher’s release settings. Grey cards are outside their approved time window.',
    liveEmpty: 'No live surveys are featured yet. Researchers can apply from their project menu (Publish to Main Page); admins approve listings on the admin dashboard.',
    liveNowOnline: 'Now online ({n})',
    liveUpcomingClosed: 'Upcoming & closed',
    liveOnline: 'Online',
    liveUpcoming: 'Upcoming',
    liveClosed: 'Closed',
    liveNoDescription: 'No description provided.',
    liveTakeSurvey: 'Take survey',
    liveNotOpenYet: 'Not open yet',

    // Team
    teamTitle: 'Team',
    teamIntroBefore: 'SP-Survey is led by',
    teamIntroMid: 'at the',
    teamIntroAfter: ', National University of Singapore, with collaborators across partner institutions and supervised by Filip Biljecki.',
    teamCollaborators: 'Collaborators',
    teamAffiliations: 'Affiliations',
    teamProfile: 'Profile',
    teamProjectLead: 'Project Lead',
    teamPI: 'Principal Investigator',
    teamLeadBlurb: 'Initiated and leads the design, development, and open release of SP-Survey — from platform architecture and survey tooling to the hosted research workflow.',
    teamPiBlurb: 'Founder of the Urban Analytics Lab; provides academic supervision and research direction for SP-Survey.',
    teamRolePhd: 'PhD Researcher',
    teamRoleEngineer: 'Research Engineer',
    teamRoleFellow: 'Research Fellow',
    teamRoleVisiting: 'Visiting Scholar',
    teamRoleAssocProf: 'Associate Professor',

    // Papers library (shell)
    papersTitle: 'Paper library',
    papersCount: '{n} papers',
    papersWithTemplate: '{n} with template',
    papersWithTemplateTitle: 'Show only papers linked to a survey template',
    papersBody: 'Curated urban imagery × human-survey literature used to ground survey templates on this platform. Linked template IDs appear when admins match a paper to an existing survey template.',
    papersAll: 'All papers',
    papersHasTemplate: 'Has template ({n})',
    papersNoTemplate: 'No template ({n})',
    papersSearchPlaceholder: 'Title / DOI / venue / author / template id',
    papersSearch: 'Search',
    papersClearSearch: 'Clear search',
    papersShowing: 'Showing {shown} / {total} papers',
    papersHasTemplateSuffix: ' · has template',
    papersNoTemplateSuffix: ' · no template',
    papersNoMatch: 'No matching papers.',
    papersOpen: 'Open paper',
    papersColTitle: 'Title',
    papersColYear: 'Year',
    papersColTemplate: 'Template',
    papersColLink: 'Link',

    // Shared request form chrome
    reqCopied: 'Copied',
    reqCopy: 'Copy',
    reqSubmitAnother: 'Submit another',
    reqBackHome: 'Back to home',
    reqStarting: 'Starting…',
    reqUploading: 'Uploading ({current}/{total})…',
    reqSubmitting: 'Submitting…',
    reqSubmit: 'Submit request',
    reqChooseFiles: 'Choose files',
    reqClear: 'Clear',
    reqFilesSelected: '{n} file(s) selected',
    reqMore: '+{n} more',
    reqMax: 'max {n}',
    reqUploaded: ' uploaded.',
    reqFileTooLarge: '"{name}" is too large (max {mb} MB).',
    reqUploadUnavailable: 'File upload is not available right now. Submit without files, or try again later.',

    // Request template
    reqTplTitle: 'Request a Template for Your Paper',
    reqTplIntro: 'No account needed. Share your paper details, optional survey dataset images, and supplementary files (PDF, etc.). We create a pending template ID you can use after review. Need help designing a custom study instead?',
    reqTplLinkDesign: 'Request survey design',
    reqTplSuccess: 'Request submitted. Your pending template ID is ready — save it.',
    reqTplId: 'Template ID',
    reqTplImagesCount: '{n} survey dataset image(s)',
    reqTplSuppCount: '{n} supplementary file(s)',
    reqTplStatus: 'Status: pending review (not on the public library yet). After approval, open the researcher panel and use this template ID.',
    reqTplNeedFields: 'Please fill in paper title, authors, and paper link.',
    reqTplBadYear: 'Year must be a 4-digit year (e.g. 2025).',
    reqTplBadEmail: 'Please enter a valid email, or leave it blank.',
    reqTplPaperTitle: 'Paper title',
    reqTplAuthors: 'Author name(s)',
    reqTplAuthorsHelp: 'e.g. Yang, Sijie and Chong, Adrian and Liu, Pengyuan and Biljecki, Filip',
    reqTplYear: 'Year',
    reqTplYearPh: 'e.g. 2025',
    reqTplLink: 'Paper link',
    reqTplLinkPh: 'https://doi.org/… or publisher URL',
    reqTplEmail: 'Email (optional)',
    reqTplEmailHelp: 'Optional — so we can reach you about this request. No account required.',
    reqTplNotes: 'Notes (optional)',
    reqTplNotesHelp: 'Method details, survey design hints, Hugging Face dataset, etc.',
    reqTplImages: 'Survey dataset images (optional)',
    reqTplImagesHelp: 'Images are auto-compressed before upload.',
    reqTplChooseImages: 'Choose images',
    reqTplSupp: 'Supplementary files (optional)',
    reqTplSuppHelp: 'PDF, Word, slides, spreadsheets, ZIP, etc. — up to {mb} MB each.',

    // Request survey design
    reqDesTitle: 'Request Survey Design',
    reqDesIntro: 'Planning a perception study and want help turning it into an SP-Survey? Share a short research brief — this is best-effort research collaboration, not a paid service. Prefer to convert a published paper into a reusable template?',
    reqDesLinkTpl: 'Request a template instead',
    reqDesSuccess: 'Request submitted. Save your request ID — we will follow up by email when we can help.',
    reqDesId: 'Request ID',
    reqDesMediaCount: '{n} media file(s)',
    reqDesSuppCount: '{n} supplementary file(s)',
    reqDesStatus: 'Status: pending. We review requests as capacity allows and will email you if we can help design or scaffold your survey.',
    reqDesStartSelf: 'Or start building yourself',
    reqDesNeedFields: 'Please fill in name, email, study title, and research brief.',
    reqDesBadEmail: 'Please enter a valid email so we can follow up.',
    reqDesBriefShort: 'Please describe your study in a bit more detail (at least a few sentences).',
    reqDesName: 'Your name',
    reqDesEmail: 'Email',
    reqDesEmailHelp: 'Required so we can follow up.',
    reqDesAffiliation: 'Affiliation (optional)',
    reqDesAffiliationPh: 'e.g. Urban Analytics Lab, NUS',
    reqDesStudyTitle: 'Study title',
    reqDesBrief: 'Research brief',
    reqDesBriefHelp: 'What do you want to measure, with whom, and roughly how? A few sentences is enough.',
    reqDesStimulus: 'Stimulus media (optional)',
    reqDesStimulusHelp: 'What participants will see or hear.',
    reqDesStimImage: 'Images',
    reqDesStimVideo: 'Video',
    reqDesStimAudio: 'Audio',
    reqDesStimMixed: 'Mixed media',
    reqDesStimOther: 'Other / not sure yet',
    reqDesTimeline: 'Timeline (optional)',
    reqDesTimelinePh: 'e.g. pilot in August, full run in October',
    reqDesRelated: 'Related link (optional)',
    reqDesRelatedPh: 'Paper, project page, or dataset URL',
    reqDesNotes: 'Additional notes (optional)',
    reqDesNotesHelp: 'Question types you have in mind, sample size targets, constraints, etc.',
    reqDesSampleMedia: 'Sample media (optional)',
    reqDesSampleHelp: 'Images, video, or audio examples. Images are auto-compressed before upload.',
    reqDesChooseMedia: 'Choose media',
    reqDesSupp: 'Supplementary files (optional)',
    reqDesSuppHelp: 'Protocol notes, PDF, slides, spreadsheets, ZIP, etc. — up to {mb} MB each.',
    reqDesInfo: 'Best-effort support from the SP-Survey team as capacity allows. You can also create an account and build the survey yourself anytime.',

    // Legacy / China
    regionLabel: 'Region',
    globalMode: 'Global',
    chinaMode: 'China',
    languageToggle: '中文',
    imageDataset: 'Step 1 - Media Dataset',
    datasetProvider: 'Dataset Provider',
    huggingface: 'Hugging Face',
    modelscope: 'ModelScope (魔搭)',
    msToken: 'ModelScope Access Token (Optional)',
    hfDatasetName: 'HuggingFace Dataset Name',
    msDatasetName: 'ModelScope Dataset Name',
    msPlaceholder: 'e.g. sijiey/Thermal-Affordance-Dataset',
    hfTokenHelp: 'Optional: only for private datasets. Get from huggingface.co/settings/tokens',
    msTokenHelp: 'Optional: only for private datasets. Get from modelscope.cn/my/myaccesstoken',
    hfBrowse: 'Browse HuggingFace Datasets',
    msBrowse: 'Browse ModelScope Datasets',
    deploymentGuide: 'Deployment Guide',
    globalDeployment: 'Vercel Deployment',
    chinaDeployment: 'Zeabur Deployment (China)',
    storageProvider: 'Image Storage Provider',
    supabase: 'Supabase Storage',
    aliyunOss: 'Alibaba Cloud OSS',
    chinaModeBanner: 'China Mode enabled — Using ModelScope, Alibaba Cloud OSS, and Zeabur',

    // Platform admin → Templates: online → builtin ZIP
    tplExportAll: 'Export all online templates',
    tplExportAllTooltip: 'Download every Supabase template as a built-in ZIP (JSON + index.json, no images). Unzip into public/project_templates/ and commit. _export/manifest.json records export time and each template\'s updated_at.',
    tplExportAllDone: 'Exported {count} online templates as a built-in ZIP — unzip into public/project_templates/ and commit',
    tplExportAllSkipped: 'Skipped {count} without a name or config: {ids}',
    tplExportAllLog: 'Exported all online templates → repo ZIP ({count}): {files}',
    tplSeedSelectAll: 'Select all',
    tplSeedConflict: 'Edited online after export',
    tplSeedConflictDetail: 'Online updated {online}, export snapshot {exported}. Not selected by default — tick it to overwrite the newer online version.',
    tplSeedConflictSummary: '{count} template(s) were edited online after the export ({exportedAt}). They stay unselected; tick one to overwrite it.',
    tplSeedConflictChip: 'Conflicts {count}',
    tplSeedPending: 'Pending review',
    tplSeedHidden: 'Not on landing',
    tplSeedKeepsStatus: 'Existing templates keep their approval, landing visibility and submitter. New templates keep the status from the export (pending stays pending).',
  },

  zh: {
    tabIntro: '简介',
    tabMedia: '1. 媒体',
    tabBuilder: '2. 设计',
    tabShare: '3. 分享',
    tabResults: '4. 结果',
    tabPractice: '试填',
    tabSilicon: '硅基',
    siliconPageTitle: '硅基样本预试',
    siliconDescription: '使用模拟参与者预试问卷。',
    siliconRunDetails: '运行说明',
    siliconNoRuns: '暂无运行记录。选择人设与题目后即可开始预试。',

    toggleSidebar: '打开/关闭项目侧栏',
    saveTooltipDirty: '保存草稿修改',
    saveTooltip: '保存',
    aiTooltip: '打开 AI 助手侧栏 — 模型与 MCP 仍在设置中管理',
    aiLabel: 'AI',
    toggleAiSidebarOpen: '关闭 AI 助手',
    toggleAiSidebarClosed: '打开 AI 助手',
    aiSidebarTitle: 'AI 助手',
    aiSidebarSubtitle: '通过对话设计当前项目',
    aiSidebarSelectProject: '请先选择一个项目再发送消息。',
    aiSidebarNoProject: '请先选择项目再开始对话。',
    aiSidebarNotConnected: '未连接 — 打开设置添加 API 密钥。',
    aiSidebarModelUnavailable: '发送前请选择已配置的模型。之前的提供商可能已被移除。',
    aiSidebarSettings: '助手设置',
    aiSidebarSilicon: '硅基预测试',
    aiSidebarClear: '清空历史',
    aiSidebarDownload: '下载对话',
    aiSidebarClose: '关闭 AI 侧栏',
    aiSidebarMore: '更多操作',
    aiSidebarEmptyTitle: '让助手帮你设计这份问卷',
    aiSidebarEmptyReady: 'AI 助手已就绪。描述你想要的问卷或修改即可。',
    aiSidebarEmptyConnect: '打开设置连接 API 密钥，或通过集成使用 Codex / MCP。',
    aiSidebarComposerPlaceholder: '输入消息…（例如「创建一份热舒适问卷」）',
    aiSidebarComposerDisabled: '请先选择项目并配置模型。',
    aiSidebarUndo: '撤销上一次 AI 修改',
    aiSidebarConnected: '已连接',
    aiSidebarDisconnected: '未连接',
    aiSidebarOpenIntegrations: '管理模型与 MCP',
    aiSidebarModel: '模型',
    aiSidebarReasoning: '推理',
    aiSidebarMode: '助手模式',
    aiSidebarModeAgent: '智能体',
    aiSidebarModeGenerate: '生成',
    aiSidebarModeAdjust: '调整',
    aiSidebarModeQuestion: '问答',
    aiSidebarModeReview: '评审',
    aiReviewPanelTitle: '多智能体评审',
    aiReviewRoles: '评审角色',
    aiReviewRole_scientist: '科学家',
    aiReviewRole_participant: '参与者',
    aiReviewRole_planner: '规划师',
    aiReviewRole_psychologist: '心理学家',
    aiReviewRole_analyst: '分析师',
    aiReviewRevision: '修订',
    aiReviewMethod: '方式',
    aiReviewMethodLinear: '逐一独立评审',
    aiReviewMethodGroup: '小组讨论',
    aiReviewMaxRounds: '最多轮数',
    aiReviewThreshold: '通过分数',
    aiReviewMaxRoles: '角色上限',
    aiReviewApplyMode: '修订',
    aiReviewApplyModeReview: '只评审，不应用',
    aiReviewApplyModeApply: '每轮自动应用',
    aiReviewEstimate: '预估：{calls} 次模型调用，{minTokens}–{maxTokens} tokens',
    aiReviewEstimateCost: '约 {min}–{max}',
    aiReviewEstimateNoPrice: '该模型暂无价格信息',
    aiReviewPlaceholder: '可选：希望评审重点关注什么？直接点发送即可开始评审。',
    aiReviewRound: '第 {round} 轮',
    aiReviewAverage: '平均 {rating}/10（{threshold} 分通过）',
    aiReviewDecisionAccept: '通过',
    aiReviewDecisionRevise: '需修订',
    aiReviewDecisionFailed: '所有评审均失败',
    aiReviewRoleRunning: '评审中…',
    aiReviewRoleFailed: '失败',
    aiReviewRoleCancelled: '已取消',
    aiReviewStrengths: '优点',
    aiReviewConcerns: '问题',
    aiReviewSuggestions: '建议',
    aiReviewSummary: '总结',
    aiReviewPlan: '修订计划',
    aiReviewOperations: '{count} 项操作',
    aiReviewApplyRound: '应用第 {round} 轮',
    aiReviewApplyAll: '应用全部修订',
    aiReviewApplied: '已应用',
    aiReviewRevising: '修订中…',
    aiReviewRevisionInvalid: '修订未通过校验',
    aiReviewConflict: '评审期间草稿已变更，未应用',
    aiReviewStatus_running: '评审进行中',
    aiReviewStatus_accepted: '已通过',
    aiReviewStatus_max_rounds: '已达最多轮数',
    aiReviewStatus_failed: '评审失败',
    aiReviewStatus_revision_failed: '修订失败',
    aiReviewStatus_revision_invalid: '修订未通过校验',
    aiReviewStatus_conflict: '评审期间草稿已变更',
    aiReviewStatus_apply_failed: '应用修订失败',
    aiReviewStatus_budget_exhausted: '已达 token 上限',
    aiReviewStatus_cancelled: '已取消',
    aiReviewStatusRound: '第 {round} 轮评审：{role}…',
    aiReviewStatusRevising: '第 {round} 轮评审：修订中…',
    aiReviewQuestions: '相关题目：{names}',
    aiReviewSettingsEnable: '在助手输入框中显示「评审」模式',
    aiReviewSettingsRoles: '默认评审角色',
    aiReviewSettingsOverrideNote: '这些是你的默认设置。助手输入框中的评审选项仍可在单次运行时修改。',
    aiReviewSettingsSaving: '正在保存…',
    aiReviewSettingsSaved: '已保存到你的账号',
    aiReviewSettingsError: '未能保存到账号，仅在当前浏览器生效。',
    aiSidebarSelectModel: '选择模型',
    aiSidebarConfigureModel: '请先配置模型',
    aiSidebarAssistantName: '助手',
    aiSidebarSend: '发送消息',
    aiSidebarStop: '停止当前任务',
    aiSidebarApprovalPrompt: '此操作需要你的批准',
    aiSidebarApprovalRisk: '风险：{risk}',
    aiSidebarApprovalProject: '项目：{project}',
    aiSidebarApprovalApprove: '批准',
    aiSidebarApprovalDeny: '拒绝',
    aiSidebarSteer: '调整方向',
    aiSidebarSteerPlaceholder: '给正在运行的智能体补充指令…',
    aiSidebarStatusThinking: '正在思考…',
    aiSidebarStatusQueued: '已排队，等待开始…',
    aiSidebarStatusRunning: '正在继续生成问卷…',
    aiSidebarStatusApproval: '等待你的批准…',
    aiSidebarStatusRetrying: '正在重试模型请求…',
    aiSidebarStatusCompacting: '正在压缩上下文并继续…',
    aiSidebarStatusVerifying: '正在核对刚保存的草稿…',
    aiSidebarStatusUsingTool: '正在调用 {tool}…',
    aiSidebarStatusStep: '正在执行第 {step} 步…',
    aiSidebarElapsed: '已用 {seconds} 秒',
    aiSidebarToolRunning: '进行中',
    aiSidebarToolDone: '完成',
    aiSidebarToolError: '失败',
    aiSidebarToolUnknown: '未知',
    aiSidebarMemoryHint: '记忆按项目隔离 •',
    aiSidebarUnnamedProject: '未命名项目',
    aiSidebarCodexTitle: 'Codex / MCP',
    aiSidebarCodexBody: '面向 Cursor、Claude 和 ChatGPT 的高级 MCP 路径。',
    aiSettingsTitle: '助手设置',
    aiSettingsModels: '模型',
    aiSettingsContext: '研究背景',
    aiSettingsAdvanced: '高级',
    aiSettingsLocalKeyIntro: '为当前浏览器连接 OpenAI 或 OpenRouter API 密钥。',
    aiSettingsValidate: '验证',
    aiSettingsContextTitle: '研究背景',
    aiSettingsContextIntro: '这些信息是可选的，用于让助手在当前项目中保持方向一致。',
    aiSettingsMemory: '项目记忆',
    aiSettingsMemoryHint: '仅在当前项目内保留对话背景和偏好。',
    aiSettingsMemoryAutomatic: '当前项目的对话连续性由托管 Agent 会话自动处理。',
    aiSettingsResearchTopic: '研究主题',
    aiSettingsResearchRequirements: '研究要求',
    aiSettingsScenario: '调查场景',
    aiSettingsCustomScenario: '添加自定义场景',
    aiSettingsAdd: '添加',
    aiSettingsAdvancedTitle: '高级',
    aiSettingsAdvancedIntro: '实验性与维护功能。大多数研究无需调整。',
    aiSettingsMultiAgent: '多智能体评审',
    aiSettingsExperimental: '实验性',
    aiSettingsMultiAgentUnavailable: '「评审」模式让多个评审角色分轮评审当前问卷并提出修订。可在此设置默认值。',
    aiSettingsMultiAgentToggle: '使用多个专家提示审查生成的问卷',
    aiSettingsIndependentReview: '独立审查',
    aiSettingsGroupReview: '小组讨论',
    aiSettingsReviewRounds: '最多审查轮数',
    aiSettingsEditAgents: '编辑专家提示',
    aiSettingsPrompts: '系统提示词',
    aiSettingsPromptsHint: '仅用于旧版本地助手',
    aiSettingsPromptGenerate: '生成问卷提示词',
    aiSettingsPromptAdjust: '调整问卷提示词',
    aiSettingsPromptIntent: '意图识别提示词',
    aiSettingsPromptQuestion: '问答提示词',
    aiSettingsReset: '重置',
    aiSettingsSave: '保存',
    aiSettingsConversationData: '对话数据',
    aiSettingsConversationDataHint: '下载当前对话，或从当前项目中清空它。',
    aiClearHistoryTitle: '清空对话',
    aiClearHistoryMessage: '清空此项目的对话历史？',
    aiClearHistoryConfirm: '清空',
    previewSurvey: '预览问卷',
    viewLive: '问卷链接',
    navMainPage: '首页',
    moreTools: '更多工作区工具',
    signedInAs: '当前登录',
    changeTheme: '切换主题',
    skillsLibrary: '自定义交互',
    adminDashboard: '管理后台',
    templatesAndProjects: '模板与项目',
    clearEditingState: '清除编辑状态',
    signOut: '退出登录',
    chooseTheme: '选择主题',
    loadingProjectSystem: '正在加载项目系统…',
    projectSystemTimeout: '项目列表加载超时。数据库可能正忙，请稍等后再刷新。',
    loadingWorkspace: '正在加载工作区…',
    noProjectTitle: '未选择项目',
    noProjectBody: '请在侧栏新建项目，或选择已有项目开始。',
    openProjectSidebar: '打开项目侧栏',
    saveStatusSaving: '保存中…',
    saveStatusError: '保存失败 — 点击保存重试',
    saveStatusUnsaved: '有未保存更改',
    saveStatusJustNow: '刚刚自动保存',
    saveStatusSecsAgo: '{n} 秒前自动保存',
    saveStatusMinsAgo: '{n} 分钟前自动保存',
    saveStatusAllSaved: '全部已保存',

    // Open project header: time up and stored answers
    surveyAgePublished: '已发布 {when} · {count}',
    surveyAgeCreated: '创建于 {when} · {count}',
    surveyAgeAnswer: '{n} 份回答',
    surveyAgeAnswers: '{n} 份回答',
    surveyAgeAnswersUnknown: '回答数暂不可用',
    surveyAgeCountLoading: '…',
    surveyAgeWhenJustNow: '刚刚',
    surveyAgeWhenMinute: '{n} 分钟前',
    surveyAgeWhenMinutes: '{n} 分钟前',
    surveyAgeWhenHour: '{n} 小时前',
    surveyAgeWhenHours: '{n} 小时前',
    surveyAgeWhenDay: '{n} 天前',
    surveyAgeWhenDays: '{n} 天前',

    openSurveyBuilder: '打开问卷构建',
    guideTitle: '快速上手 SP-Survey',
    guideBody:
      '开始创建问卷，查看项目进度。',
    guideStartTour: '开始导览',
    guideRestart: '重新开始引导',
    guideRestartHint: '重新显示项目清单和顶栏导览。',
    guideHide: '隐藏',
    guideShowQuickstart: '显示快速上手',
    guideShowChecklist: '显示清单',
    guideOpenTab: '打开「{tab}」',
    guideQuickTitle: '5 分钟完成第一份问卷',
    guideQuickLead: '最快的方式：从已发布的模板开始，再按需修改。',
    guideQuick1: '选择模板',
    guideQuick1Action: '打开项目',
    guideQuick2: '导入媒体（可选）',
    guideQuick3: '编辑题目',
    guideQuick4: '预览问卷',
    guideQuick4Action: '预览',
    guideQuick5: '发布与分享',
    guideChecklistTitle: '项目清单',
    guideChecklistProgress: '已完成 {done}/{total}',
    guideChecklistHint: '根据本项目自动勾选。预览和分享记录保存在当前浏览器。',
    guideChecklistAllDone: '全部就绪：问卷已上线，答卷正在进来。',
    guideCheckMedia: '导入媒体（可选）',
    guideCheckMediaDone: '{n} 个媒体文件',
    guideCheckMediaTodo: '纯文字问卷可跳过',
    guideCheckQuestions: '编辑题目',
    guideCheckQuestionsDone: '{n} 道作答题',
    guideCheckPreview: '预览问卷',
    guideCheckPublished: '发布问卷',
    guideCheckPublishedDone: '版本 {n} 已上线',
    guideCheckShared: '分享问卷链接',
    guideCheckResponses: '查看答卷',
    guideCheckResponsesDone: '{n} 份答卷',
    guideCheckResponsesUnknown: '打开「结果」查看',
    guideStepsTitle: '分步工作流程',
    guideStatusDone: '已完成',
    guideStatusTodo: '未开始',
    guideOptional: '可选',
    guideBeforeShare: '分享前',
    guideWhyLabel: '用途',
    guideDoLabel: '操作',
    guideMediaHeadline: '添加媒体',
    guideMediaWhy: '存放题目要展示的图片、视频和音频。纯文字问卷可跳过。',
    guideMediaDo: '上传文件或文件夹、导入 Hugging Face 数据集，或从模板导入媒体。为文件夹标记 set 或 category，题目即可按文件夹抽取。',
    guideMediaEmpty: '还没有媒体。可上传文件、从 Hugging Face 导入，或从模板导入。',
    guideBuilderHeadline: '设计页面与题目',
    guideBuilderWhy: '决定参与者看到什么：页面、题目、媒体规则和试次顺序。',
    guideBuilderDo: '添加题目（评分、选择、成对比较、排序、标注、视频等）或 Skill。编辑时可逐题预览，修改会自动保存到草稿。',
    guideBuilderEmpty: '还没有题目。可在「设计」中添加，或让 AI 助手起草问卷。',
    guideTryHeadline: '自己先试一遍',
    guideTryWhy: '在真实参与者之前发现表述不清、媒体失效或随机化错误。',
    guideTryDo: '顶栏「预览」打开与线上问卷一致的副本；「试填」可逐题作答并查看记录下来的数据。',
    guideTryEmpty: '尚未试填。发布前请完整预览一遍。',
    guidePreviewAction: '打开预览',
    guideSiliconHeadline: '用硅基样本预试',
    guideSiliconWhy: 'AI 模拟受访者，用于快速预试和假设探索，绝不会混入真人结果。',
    guideSiliconDo: '选择模型和样本量，开始运行，再查看作答。运行会在后台继续。',
    guideSiliconOff: '硅基样本已关闭。可在 AI 助手设置中开启后使用此步骤。',
    guideShareHeadline: '发布并分享',
    guideShareWhy: '把草稿变成参与者版本。问卷链接由平台托管，无需另行部署。',
    guideShareDo: '先处理发布前检查提示的问题，再发布版本（之后的发布沿用同一链接），然后复制链接或下载二维码。',
    guideShareEmpty: '尚未发布。发布后，当前草稿即成为参与者版本。',
    guideResultsHeadline: '分析答卷',
    guideResultsWhy: '把答卷整理成可导出、可汇报的逐题结果。',
    guideResultsDo: '筛选答卷、查看逐题图表；成对比较题按类别分标签显示 TrueSkill 排名；可导出 CSV/JSON，或让 AI 助手解释结果。',
    guideResultsEmpty: '还没有答卷。先分享链接，再回到这里。',
    guideAiTitle: 'AI 助手：每一步都能帮忙',
    guideAiLead: '点击顶栏「AI」，就当前项目对话。在输入框选择模式：',
    guideAiAgent: '规划并完成多步任务：读取草稿、修改、导入媒体，执行高风险操作前会先征求同意。',
    guideAiGenerate: '根据你的描述一次性起草完整问卷。',
    guideAiAdjust: '只做你要求的修改，其余内容保持不变。',
    guideAiQuestion: '只读。回答关于问卷或结果的问题，不做任何修改。',
    guideAiReview: '多个评审角色分轮为问卷打分和点评，并提出可应用的修订。',
    guideAiOpen: '打开 AI 助手',
    guideAiSetup: '设置模型',
    guideAiSetupHint: 'AI 助手使用你自己的 API 密钥（DeepSeek、OpenRouter、OpenAI 等），保存在「AI 与集成」中。',
    guideAiOff: 'AI 助手已关闭，可在 AI 助手设置中开启。',
    guideMcpTitle: '进阶：通过 MCP 使用 Codex、Cursor 或 Claude Code',
    guideMcpBody: '在「AI 与集成」中连接外部智能体，它会以相同的安全检查编辑同一份草稿。示例提示词：',
    guideMcpExample: '使用 sp_survey MCP，创建一份关于 <你的主题> 的问卷。\n加入题型 <题型说明>。\n我的媒体是 <描述>，位于 <文件夹位置>。',
    guideTourStepOf: '{n}/{total}',
    guideTourNext: '下一步',
    guideTourBack: '上一步',
    guideTourSkip: '跳过导览',
    guideTourDone: '完成',
    guideTourLabel: '工作区导览',
    guideTourProjectsTitle: '项目与模板',
    guideTourProjectsBody: '打开你的项目、新建项目，或从已发布的模板开始。',
    guideTourTabsTitle: '流程标签页',
    guideTourTabsBody: '从左到右：媒体、设计、分享、结果。「试填」和「硅基」用于测试。',
    guideTourSaveTitle: '自动保存',
    guideTourSaveBody: '修改会自动保存到草稿。只有发布后，参与者链接的内容才会变化。',
    guideTourPreviewTitle: '预览',
    guideTourPreviewBody: '以参与者视角完整作答整份问卷。',
    guideTourAiTitle: 'AI 助手',
    guideTourAiBody: '在任意标签页让助手构建或修改问卷。',
    guideTourLiveTitle: '查看线上问卷',
    guideTourLiveBody: '在新标签页打开参与者链接。',
    guideTourMoreTitle: '更多工具',
    guideTourMoreBody: '主题、Skill 库和账户。',
    guideTourRestartTitle: '随时回来',
    guideTourRestartBody: '「简介」标签页保留清单，「重新开始引导」可恢复全部提示。',

    mediaTitle: '媒体数据集',
    mediaDescription:
      '上传并整理问卷媒体。',
    mediaAddSection: '1 · 添加媒体',
    mediaOrganizeSection: '2 · 在文件夹中整理',
    mediaTaggedSection: '3 · 已标记供分配',
    mediaImportTemplateTitle: '导入模板 / 预览媒体',
    mediaImportTemplateHelp: '从已发布模板或管理端预览媒体库复制文件。已存在文件会跳过，便于断点续传。',
    mediaUploadTitle: '上传媒体',
    mediaUploadHelpPrefix: '上传到媒体库中的当前文件夹',
    mediaUploadLimitsHelp:
      '单文件上限 {mb} MB。图片可选择压缩；视频、音频和动态 GIF 保留原文件。',
    mediaAvTooLarge: '「{name}」过大（{size} MB）。视频/音频上限 {mb} MB。',
    mediaChooseFiles: '选择文件',
    mediaSelected: '已选',
    mediaUploadBtn: '上传',
    mediaUploadRoot: '→ 根目录',
    mediaNoFoldersTagged:
      '尚未标记文件夹。在媒体库左侧勾选文件夹，再点击“分组”或“分类”。标记后的文件夹会显示在这里，供问卷分配使用。',
    mediaTaggedSets: '已标记分组（{n}）',
    mediaTaggedSetsHelp:
      '标记为分组的文件夹。题目使用“随机固定分组”且媒体数量匹配时，文件夹内直属文件会一起展示。',
    mediaTaggedCategories: '已标记分类（{n}）',
    mediaTaggedCategoriesHelp: '标记为分类的文件夹。题目可从每个分类中随机抽取文件。',
    mediaCheckingR2: '正在检查 R2 中的现有图片…',
    mediaLastUpload: '最近上传：',
    hfImportTitle: 'HF 数据集导入',
    hfImportHelp:
      '从 HuggingFace 批量导入。使用 owner/dataset（按行）或 owner/dataset/folder（递归；保留嵌套文件夹）。继续导入会复用首次目标路径，避免重复嵌套。',
    hfEnable: '启用 HF 导入',
    hfToken: '令牌（可选）',
    hfDataset: '数据集',
    hfPlaceholder: 'owner/dataset',
    saveConfig: '保存',
    testConnection: '测试',
    hfPreload: '预加载到 R2',
    hfRePreload: '重新预加载到 R2',
    noMediaYet: '尚未上传媒体',
    mediaInR2: '个媒体文件在 R2',

    guideFixedSets: '固定分组',
    guideFixedSetsBody:
      '将文件夹标记为分组（Set）。每个分组文件夹的直属文件会一起展示，每组文件数须与题目的媒体数量一致。',
    guideExample: '示例',
    guideFixedSetsExample:
      '1. 创建文件夹 block01、block02、block03\n2. 每个文件夹放 2 张图（数量相同）\n3. 勾选这些文件夹 → 点击“分组”\n4. 在问卷构建中选择媒体分配方式「随机抽取完整分组」，数量 = 2\n→ 每位受访者看到一整文件夹（两张图一起）',
    guideTaggedSets: '当前已标记分组：{n}',
    guideCategories: '分类',
    guideCategoriesBody:
      '将文件夹标记为分类（Category）。在题目中选择「按分类抽取」，并设置每个分类抽取的文件数。每个分类会从该文件夹及其子文件夹中随机抽取。',
    guideCategoriesExample:
      '1. 创建文件夹 street 与 park\n2. 每个放若干图片（可用子文件夹）\n3. 勾选这些文件夹 → 点击“分类”\n4. 在题目中：媒体分配 → 按分类抽取，每类文件数 = 2\n→ 每位受访者看到 2 张 street + 2 张 park（共 4 张）',
    guideTaggedCategories: '当前已标记分类：{n}',
    guideProjectFiles: ' · 项目文件：{n}',

    builderTitle: '问卷构建',
    builderDescription:
      '设计题目，配置问卷。',
    builderSurveySettings: '问卷设置',
    builderNextShare: '下一步：分享问卷 →',
    builderBasicInfo: '基本信息',
    builderSurveyTitle: '问卷标题',
    builderSurveyTitleHelp: '显示在问卷顶部的主标题',
    builderSurveyDescription: '问卷说明',
    builderSurveyDescriptionHelp: '简要说明问卷目的',
    builderCompletionMessage: '完成提示（可选）',
    builderCompletionMessageHelp: '参与者提交后显示的内容',
    builderResponseQuota: '答卷配额（可选）',
    builderResponseQuotaHelp: '达到该答卷数后自动关闭问卷（留空表示不限）',
    builderLogoSettings: 'Logo 设置',
    builderDisplaySettings: '显示设置',
    builderSurveyLanguage: '问卷语言',
    builderSurveyLanguageHelp: '参与者看到的进度、轮次提示、下一页/提交按钮。默认英文。',
    builderSurveyLanguageEn: 'English',
    builderSurveyLanguageZh: '中文',
    builderThemeCustomization: '主题自定义',
    builderPagesQuestions: '页面与题目',
    builderPagesHelp: '将问卷组织为多个页面。可拖拽页面调整顺序。',
    builderAddPage: '添加新页面',
    builderLayoutStudio: '版式工作台',
    builderPreview: '预览',
    previewDevice: '预览设备',
    previewDesktop: 'PC 端',
    previewMobile: '手机端',
    previewSizing: '正在调整：{viewport}',
    previewFormatOnly: '这里只调整版式，不能作答。',
    previewSurveyTitle: '问卷标题',
    previewSurveyDescription: '问卷描述',
    previewEditSurveyTitle: '修改问卷标题',
    previewEditSurveyDescription: '修改问卷描述',
    previewLogoUrl: 'Logo 地址',
    previewLogoHelp: '显示在问卷页眉的图片地址',
    previewContentWidth: '内容宽度',
    previewQuestionWidth: '题目白框宽度',
    previewMediaSize: '媒体高度',
    previewQuestionText: '题目文字',
    previewQuestionDescription: '题目说明',
    previewPageTitle: '页面标题',
    previewPageDescription: '页面说明',
    previewMoveUp: '上移',
    previewMoveDown: '下移',
    previewReorderQuestion: '第 {index} 题，共 {count} 题：{name}',
    previewReorderPage: '第 {index} 页，共 {count} 页：{name}',
    previewMoveQuestionUp: '上移题目',
    previewMoveQuestionDown: '下移题目',
    previewMovePageUp: '上移页面',
    previewMovePageDown: '下移页面',
    previewMediaAssignment: '媒体分配',

    shareTitle: '分享问卷',
    shareDescription: '发布问卷，邀请参与者作答。',
    shareYourLink: '你的问卷链接',
    shareCopied: '已复制！',
    shareCopyLink: '复制链接',
    shareOpenSurvey: '打开问卷',
    shareQrAlt: '问卷二维码',
    shareQrDownload: '下载二维码（PNG）',
    shareQrHint: '扫码打开此问卷。可下载 1024 × 1024 PNG，用于分享或打印。',
    shareQrLoading: '正在生成二维码',
    shareQrError: '二维码生成失败。',
    shareQrRetry: '重新生成',
    shareQrLocalHint: '当前为本地预览，请在线上平台分享问卷。',
    shareNoProject: '未选择项目。请先选择或创建项目。',
    shareTips: '💡 分享提示',
    shareTip1Primary: '直接分享链接',
    shareTip1Secondary: '可通过邮件、微信、Slack 等渠道发送',
    shareTip2Primary: '做成二维码',
    shareTip2Secondary: '在上方“你的问卷链接”中下载二维码，可用于邀请、幻灯片或印刷材料',
    shareTip3Primary: '同一个研究使用同一个链接',
    shareTip3Secondary: '启用版本管理后，发布正式版本才会更新链接；保存只更新草稿。',
    shareTip4Primary: '答卷自动保存',
    shareTip4Secondary: '结果实时写入 Supabase，可在结果分析标签页查看',
    shareAdminLink: '🔒 管理面板链接',
    shareAdminHelp: '分享给需要编辑问卷的协作者；对方需登录。',
    shareCopyAdmin: '复制管理链接',
    shareCustomLink: '自定义链接',
    shareCustomLinkHint: '为这份问卷设置一个简短的公开地址。原来的项目链接仍然可用。',
    shareCustomLinkLabel: '自定义链接名称',
    shareCustomLinkSave: '保存链接',
    shareCustomLinkSaving: '正在保存…',
    shareCustomLinkSaved: '自定义链接已保存。',
    shareCustomLinkCleared: '已移除自定义链接。参与者仍可使用项目链接。',
    shareCustomLinkHelp: '2–40 个字符：小写字母、数字，连字符不能连续或出现在首尾。保留名称：admin、api、s、survey、login。留空后保存即可移除。',
    shareCustomLinkInvalid: '请使用 2–40 个字符：小写字母、数字，连字符不能连续或出现在首尾。',
    shareCustomLinkReserved: '该名称是保留字，请换一个。',
    shareCustomLinkTaken: '这个链接已被其他问卷使用。',
    shareCustomLinkForbidden: '只有项目所有者可以修改这个链接。',
    shareCustomLinkUnavailable: '当前数据库还没有启用自定义链接。请在 Supabase SQL 编辑器中粘贴 supabase/project_public_slug.sql，然后重试。',
    shareCustomLinkFailed: '自定义链接保存失败。',
    shareCustomLinkKeepId: '原来的项目链接仍然可用：',

    resultsScopeHint: "当前分析和导出包含 {shown} / {total} 份提交。",
    resultsExportHelp: '表格每行一份提交；完整分析包另含逐轮长表、汇总、方法说明和已保存的分析报告。',
    resultsExportSiteHelp: '平台未做分析。ZIP 里是回答表格和原样保存的全部记录，供你自行分析。',
    resultsBrowserPrefs: "筛选偏好仅保存在当前浏览器，不会修改问卷。",
    resultsResetFilters: "重置筛选",
    resultsFilteredEmpty: "没有符合当前筛选条件的提交。重置筛选可查看全部答卷。",
    resultsPreparingExport: "正在准备导出…",
    resultsViewResponse: "查看",
    resultsSubmissionDetail: "提交详情",
    resultsHistoricalRevision: "未记录（历史答卷）",
    resultsGroupExplanation: "一次回答评价整组展示媒体，每种有序媒体组合只计一次。",
    resultsCounts: "已回答 {answered} / {total} 份提交（{rate}%）· {people} 位参与者",
    resultsRecordTrial: "轮次",
    resultsNoMedia: "无媒体",
    resultsClose: "关闭",
    resultsRevision: "问卷版本",
    resultsQuality: "质量标记",
    resultsDetailSession: "会话",
    resultsMixedRevisions: "这些答卷涉及多个问卷版本。分析和导出仅使用当前所选版本，避免新旧设置混算。可在下方切换；未记录版本的历史答卷仍标为未知。",
    resultsAllRevisions: "全部版本",
    resultsRowsPerPage: "每页行数",
    resultsTitle: '结果分析',
    resultsDescription: '查看、分析并导出答卷。',
    resultsWorkspace: '分析工作区',
    resultsViews: '分析视图',
    resultsAttributeFilters: '{n} 项参与者属性筛选',
    resultsDescriptionPrefix: '按题目分析项目答卷：',
    resultsRefresh: '刷新答卷',
    resultsLoadProgress: '正在加载答卷… {loaded} / {total}',
    resultsLoadStarting: '正在读取第一页答卷…',
    resultsLoadProgressUnknown: '正在加载答卷… 已读取 {loaded} 份',
    resultsAnalysisProgress: '正在分析结果… {loaded} / {total}',
    resultsExportCsv: '导出 CSV',
    resultsExportAll: '全部导出',
    resultsExportMethods: '导出方法说明',
    resultsViewOverview: '概览',
    resultsViewQuestions: '题目',
    resultsViewData: '数据',
    resultsAnalyze: '分析当前结果',
    resultsExplainQuestion: '解释这道题',
    resultsFilters: '筛选',
    resultsExportMenu: '导出',
    resultsExportReport: '本次分析报告',
    resultsExportSummary: '汇总统计',
    resultsExportRaw: '原始／逐轮数据',
    resultsExportBundle: '完整分析包（ZIP）',
    resultsExportTable: '回答表格（CSV）',
    resultsExportSiteAnswers: '全部回答（ZIP）',
    resultsParticipants: '独立参与者',
    resultsTrials: '回答轮次',
    resultsLatestReport: '最近分析',
    resultsViewReport: '查看报告',
    resultsDeleteReport: '删除报告',
    resultsCancel: '取消',
    resultsIncompleteReport: '分析未完成',
    resultsReportIncompleteHelp: '此次分析未生成完整报告，请重新运行分析。',
    resultsDeleteReportConfirm: '删除这份已保存的分析报告？调查答卷将保留。',
    resultsReportSaveError: '分析报告保存失败，请重试。',
    resultsReportDeleteError: '报告删除失败，请重试。',
    resultsNoReport: '还没有分析报告。点击“分析当前结果”生成。',
    resultsReportStaleScope: '这份报告来自之前的分析范围。请重新分析以更新。',
    resultsReportStaleData: '报告之后又有新答卷。请重新分析以更新。',
    resultsViewEvidence: '查看依据',
    resultsDeepPerception: '标注 × 感知 深入分析',
    resultsOpenPerception: '打开深入分析',
    resultsDataSource: '数据来源',
    resultsSourceHuman: '真人答卷',
    resultsSourcePractice: '仅研究者练习',
    resultsSourceSilicon: 'Silicon 运行',
    resultsSiliconRun: 'Silicon 运行 ID',
    resultsApplyFilters: '应用',
    resultsScopeNow: '当前范围',
    resultsSelectQuestion: '选择题目',
    resultsQuestionSearch: '搜索题目',
    resultsDateTimezone: '日期按 {tz} 计算。启用日期筛选时，没有有效时间戳的记录不纳入。',
    resultsQualityIsInfo: '质量标记只是检查信息，不会自动删除答卷。',
    resultsContractFallback: '部分历史答卷没有冻结题目合同，当前分析使用现有设置作为替代。',
    resultsFormalHuman: '正式分析默认使用真人数据；练习或 Silicon 需明确选择。',
    resultsConnectedSupabase: '已连接 Supabase',
    resultsLocalFiles: '读取本地答卷文件',
    resultsDataQuality: '数据质量',
    resultsClean: '{n} 条干净',
    resultsFlagged: '{n} 条标记',
    resultsInAnalysis: '{n} 条纳入分析',
    resultsExcludeFlagged: '分析时排除已标记答卷',
    resultsTotalResponses: '答卷总数',
    resultsAnswerableQuestions: '可答题数',
    resultsWithResponses: '有作答',
    resultsQuestionCoverage: '题目覆盖率',
    resultsCoverageHelp: '至少有 1 条作答的可答题',
    resultsResponseRecords: '答卷记录',
    resultsSubmissions: '{n} 份提交',
    resultsRecordsHelp: '删除本项目的单条提交。删除后不可恢复。',
    resultsRecordsViewHelp: '查看每份提交的答案、逐轮详情及质量标记。',
    resultsEmpty: '尚无答卷。分享问卷链接后，再回到这里查看结果。',
    resultsSkillCustomHelp:
      '自定义 Skill 结果按声明类型复用原生分析：标注类（points/path/polygon/bbox）叠加热力/图形；媒体评分类按图片排序；文字/排序/分配类用对应原生图表。',
    resultsSkillByMode: '按任务模式查看',
    resultsSkillByModeHelp:
      '此 Skill 把多种任务写在同一个题里，用 mode 区分（例如线索排序 / 路线描绘）。',
    resultsSkillFieldStats: '字段统计',
    resultsSkillResponseList: '回答一览',
    resultsSkillExampleFirst: '示例（第 1 条）',
    resultsSkillReadableSummary: '可读摘要',
    resultsSkillHideAllRaw: '隐藏全部原始回答',
    resultsSkillShowAllRaw: '查看全部原始回答',
    resultsSkillHideRawJson: '隐藏原始 JSON',
    resultsSkillShowRawJson: '显示原始 JSON',
    resultsSkillReadableHint: '每人一条可读摘要；需要时可展开原始 JSON。',
    resultsSkillRawHint: '仅显示答案字段；刺激媒体在每条下方单独列出。',
    resultsSkillResponseN: '回答 {n}',
    resultsSkillStimulusMedia: '刺激媒体：',
    resultsSkillShowLess: '收起',
    resultsSkillShowAllN: '查看全部 {n} 条',
    resultsSkillNoResponses: '尚无回答。',
    resultsSkillHideRaw: '隐藏原始回答',
    resultsSkillViewRaw: '查看原始回答',
    skillAnswerSubmittedTitle: '已提交的回答',
    skillAnswerYourSubmittedTitle: '你刚才提交的回答',
    skillAnswerRecorded: '已记录',
    skillAnswerEmpty: '（未作答）',
    skillAnswerNotRecorded: '此题尚未记录到答案（完成键未成功写入，或未作答）。',

    practiceTitle: '研究者练习',
    practiceDescription: '正式调查前试答并检查题目。',
    practiceQuestions: '题目',
    practiceStartSession: '开始练习会话',
    practiceSessionLocked: '会话已锁定 — 按队列作答，或结束会话后自由选题。',
    practiceFreePick: '选择题目自由作答，或开始连续练习。',
    practiceChooseQuestion: '选择一道题开始练习。',
    practiceTotal: '练习总数：{n}',
    practiceNewRound: '新一轮',
    practiceEndSession: '结束会话',
    practiceEditSettings: '题目设置',
    practiceSettingsSaved: '题目设置已更新。请保存项目以持久化。',
    practiceSettingsUnavailable: '缺少问卷配置，无法编辑题目设置。',

    sidebarProjects: '项目',
    sidebarTemplates: '项目模板',
    sidebarSearchTemplates: '搜索模板…',
    sidebarClearSearch: '清除搜索',
    sidebarTemplateFilters: '筛选和排序模板',
    sidebarCategory: '分类',
    sidebarAllCategories: '全部分类',
    sidebarSort: '排序',
    sidebarNewest: '年份从新到旧',
    sidebarOldest: '年份从旧到新',
    sidebarNameAZ: '名称 A–Z',
    sidebarNameZA: '名称 Z–A',
    sidebarTemplateCount: '显示 {shown} / {total} 个模板',
    sidebarShowMore: '显示更多',
    sidebarShowLess: '收起',
    sidebarMyProjects: '我的项目（{n}）',
    sidebarImportProject: '导入项目（.json 或 Qualtrics .qsf）',
    sidebarCreateProject: '新建项目',
    sidebarNoProjects: '还没有项目。创建你的第一个项目吧！',
    sidebarNoTemplates: '暂无模板。可从项目另存为模板。',
    sidebarNoTemplateMatch: '没有匹配的模板。',
    sidebarProjectName: '项目名称',
    sidebarDescriptionOptional: '描述（可选）',
    sidebarCancel: '取消',
    sidebarCreate: '创建',
    sidebarEditProject: '编辑项目',
    sidebarDuplicate: '复制',
    sidebarExportProject: '导出项目',
    sidebarSaveAsTemplate: '另存为模板',
    sidebarPublishMain: '发布到主页',
    sidebarDelete: '删除',
    sidebarShared: '共享',
    collaboratorTitle: '协作者',
    collaboratorEmail: '已有账号的邮箱',
    collaboratorEmailInvalid: '请输入有效的邮箱地址。',
    collaboratorAdd: '添加协作者',
    collaboratorInviteTooltip: '通过邮箱邀请协作者',
    collaboratorRemove: '移除 {email}',
    collaboratorAdded: '已添加 {email}',
    collaboratorRemoved: '已移除协作者',
    collaboratorNoAccount: '这个邮箱还没有账号。对方需要先注册。',
    collaboratorOwnerOnly: '只有项目所有者可以更改协作者。',
    collaboratorAlreadyOwner: '对方已经是这个项目的所有者。',
    collaboratorMigrationMissing: '请先在 Supabase SQL 编辑器中执行一次 supabase/project_collaborators.sql，然后再试。',
    collaboratorSaveFailed: '无法更新协作者',
    presenceTitle: '也在此项目',
    presenceOnlyYou: '目前只有你打开了这个项目',
    presencePerson: '{name}正在查看此项目',

    integBackAdmin: '返回 Admin',
    integTitle: 'AI 与集成',
    integBody:
      '优先使用站内助手和你自己的 API 密钥（推荐 DeepSeek）。Codex / Claude / Cursor 的 MCP 是高级选项。',
    integConnectTitle: '高级 — 连接 ChatGPT（Codex）',
    integAdvanced: '高级',
    integAssistantFirstBody: '填入各提供方的 API 密钥即可使用其模型。保存后密钥只写不回读，交互与 DeepSeek Harness 一致。',
    integAssistantModel: '助手模型',
    integSiliconModel: '硅基 VLM',
    integRecommended: '推荐',
    integConnected: '已连接',
    integConnectBody:
      '用 ChatGPT（Codex）创建与编辑问卷。保存后分享链接立即更新。本站无需填写 OpenAI API 密钥。',
    integMcpEndpoint: 'MCP 地址（Codex / Claude Code / Cursor 共用）：',
    integLocalMcpHint: '本地开发 MCP（仅调试用）',
    integStep1Title: '将 ChatGPT（Codex）权限设为 “Approve for me”',
    integStep1Body:
      '在 ChatGPT（Codex）Work 输入区，点击左下角权限控件，选择 Approve for me。这样 ChatGPT（Codex）才能编辑 ~/.codex/config.toml、运行 codex mcp login、打开浏览器并保存凭证。',
    integStep2Title: '粘贴到 ChatGPT（Codex）',
    integStep2Body: '复制到一个新的 ChatGPT（Codex）对话：',
    integStep3Title: '开新对话，然后设计问卷',
    integStep3Body: '打开一个新的 ChatGPT（Codex）对话，然后粘贴：',
    integClaudeTitle: '2. 连接 Claude Code',
    integClaudeBody:
      '在终端用 Claude Code 连接同一 sp_survey MCP。保存后分享链接立即更新。首次需在浏览器完成 OAuth 授权。',
    integClaudeStep1Title: '添加 MCP 服务器',
    integClaudeStep1Body:
      '在终端执行下面命令（或把提示词粘贴给 Claude Code）。建议用 user scope，这样所有项目都能用：',
    integOrPastePrompt: '或把下面设置提示词粘贴给 agent：',
    integClaudeStep2Title: '在 Claude Code 中授权',
    integClaudeStep2Body:
      '运行 /mcp，选择 sp_survey，再选 Authenticate。在系统浏览器打开授权页并在本站 Approve。若工具未出现，重启 Claude Code。',
    integClaudeStep3Title: '设计问卷',
    integClaudeStep3Body: '在新的 Claude Code 会话中粘贴：',
    integCursorTitle: '3. 连接 Cursor',
    integCursorBody:
      '在 Cursor Agent 中使用同一 sp_survey MCP。写入 JSON 配置后，到 Settings → Tools & MCP 完成 Connect / Authenticate。',
    integCursorStep1Title: '写入 ~/.cursor/mcp.json（或项目 .cursor/mcp.json）',
    integCursorStep1Body:
      '合并进已有 mcpServers，不要清空其他服务器。Cursor 只需 url（不要加 type）：',
    integCursorStep2Title: '在 Cursor 中授权',
    integCursorStep2Body:
      '打开 Cursor Settings → Tools & MCP，找到 sp_survey，点击 Connect / Authenticate。在浏览器打开本站并 Approve。若工具仍为灰色，Reload Window。',
    integCursorStep3Title: '设计问卷',
    integCursorStep3Body: '在新的 Cursor Agent 对话中粘贴：',
    integAccounts: '已连接的 MCP 账号',
    integNoneYet: '尚无连接。请先完成上面 Codex、Claude Code 或 Cursor 的连接步骤。',
    integLastUsed: '最近使用：',
    integNever: '从未',
    integDisconnect: '断开连接',
    integAssistantTitle: '1. 站内 AI 助手',
    integReady: '已就绪',
    integAssistantBody: '想留在浏览器里？添加 OpenAI 或 OpenRouter API 密钥后，可在问卷构建中使用助手。',
    integGetKey: '从 OpenAI 或 OpenRouter 获取密钥。',
    integApiKeyLabel: 'OpenAI 或 OpenRouter API 密钥',
    integSaveKey: '保存密钥',
    integRemoveKey: '移除密钥',
    integAfterSave: '保存后返回 Admin，打开项目，并展开 AI 助手面板。',
    integCopy: '复制',
    modelsTitle: '模型',
    modelsIntro: '填入各提供方的 API 密钥即可使用其模型。',
    modelsEdit: '编辑',
    modelsAdd: '添加提供方',
    modelsProvider: '提供方',
    modelsCancel: '取消',
    modelsApply: '保存',
    modelsApplying: '保存中…',
    modelsSavedProvider: '已保存 {provider}。',
    modelsCredentialConfigured: 'API 密钥已配置',
    modelsCredentialMissing: 'API 密钥缺失',
    modelsLoadFailed: '加载提供方目录失败',
    modelsReload: '重试',
    modelsKeyInput: 'API 密钥',
    modelsKeyPlaceholder: '输入 API 密钥',
    modelsKeyStored: '已配置——输入新值可替换',
    modelsCustomized: '自定义设置',
    modelsBaseUrl: 'API 地址',
    modelsBaseUrlDefault: '提供方默认',
    modelsModels: '模型目录',
    modelsSearchModels: '搜索模型',
    modelsSearchModelsHint: '按模型名称或 ID 搜索',
    modelsNoMatchingModels: '没有匹配的模型。',
    modelsAddModel: '添加模型',
    modelsRemoveModel: '删除模型',
    modelsModelId: '模型 ID',
    modelsModelName: '显示名称',
    modelsModelNamePlaceholder: '留空时使用模型 ID',
    modelsFetchModels: '获取可用模型',
    modelsFetching: '正在询问提供方…',
    modelsFetchNeedsBaseUrl: '请先填写 API 地址，再获取。',
    modelsFetchEmpty: '该提供方没有列出任何模型，请手动添加。',
    modelsFetchTitle: '选择要添加的模型',
    modelsFetchDescription: '以下是模型提供方的可用模型，勾选要添加的模型。',
    modelsFetchSelectAll: '全选',
    modelsFetchDeselectAll: '取消全选',
    modelsFetchAdopt: '添加所选',
    modelsCustomAdd: '添加自定义提供方',
    modelsCustomTitle: '自定义提供方',
    modelsCustomTag: '自定义',
    modelsCustomRoute: 'Provider ID',
    modelsCustomRouteHint: '以小写字母开头的标识，在请求中唯一标识该提供方，并用于派生凭据名。',
    modelsCustomRouteInvalid: '需以小写字母开头，之后可用小写字母、数字和短横线。',
    modelsCustomRouteTaken: '已有提供方使用了这个 ID。',
    modelsCustomDisplayName: '显示名称',
    modelsCustomApi: 'API 协议',
    modelsCustomNeedsBaseUrl: '自定义提供方需要填写 API 地址。',
    modelsCustomNeedsModels: '自定义提供方至少需要一个模型。',
    modelsCustomBaseUrlPlaceholder: 'https://gateway.example/v1',
    modelsCreate: '创建提供方',
    modelsCreating: '创建中…',
    modelsOnboardingTitle: '添加一个 API Key 开始使用',
    modelsKeyRequired: '请输入 API 密钥后继续。',
    modelsSelectModel: '选择模型',
    modelsSelectVision: '选择视觉模型',
    modelsDefaults: '默认模型',
    modelsDeleteDescriptionWithCredential: '删除 {provider} 会移除其配置和存储的 API 密钥。',
    modelsAuthUnsupported: '本轮不支持配置原生认证',
    modelsGroupRecommended: '推荐',
    modelsGroupConfigured: '已配置',
    modelsGroupCatalog: '目录',
    modelsGroupCustom: '自定义',
    modelsGroupUnsupported: '原生认证（暂不可配置）',
    modelsContextWindow: '上下文窗口',
    modelsMaxOutput: '最大输出',
    modelsInputText: '文本',
    modelsInputImage: '图像',
    modelsReasoning: '推理',
    modelsReasoningOff: '关闭',
    modelsDefaultEffort: '默认推理级别',
    modelsRetry: '最大重试次数',
    modelsSettingsConflict: '设置已被他人更改。请关闭后重新打开以编辑当前值。',
    modelsDiscovery401: '获取可用模型返回 401。',
    modelsDiscoveryUnsupported: '该协议不支持模型发现。',
    modelsDiscoveryTooLarge: '提供方响应超过 4 MiB。',
    modelsDiscoveryMalformed: '提供方返回了无法解析的 JSON。',
    modelsDiscoveryNetwork: '无法连接提供方。请检查地址后重试。',
    siliconOverrideModel: '本次运行的视觉模型',
    siliconEffort: '推理级别',
    siliconDisclaimer: '硅基样本仅用于预试与假设探索，不会计入真人配额，也不会进入默认结果分析，更不能证明设计已经科学有效。',
    siliconPersonas: '人设',
    siliconQuestions: '题目选择',
    siliconRunSettings: '运行设置',
    siliconQuestionNumber: '第 {n} 题',
    siliconSearchQuestions: '搜索题号或题目',
    siliconSelectAll: '全选支持的题目',
    siliconClearSelection: '清空选择',
    siliconSelectedCount: '已选 {n} 项',
    siliconNoMatchingQuestions: '没有匹配的题目。',
    siliconRemoveFailed: '无法删除人设。',

    siliconName: '名称',
    siliconCity: '城市 / 国家',
    siliconNotes: '情景备注',
    siliconAddPersona: '添加人设',
    siliconRemove: '删除',
    siliconRun: '运行',
    siliconRepeats: '重复次数',
    siliconBudget: 'Token 预算',
    siliconDraftSnapshot: '每次运行都会固化当前已保存草稿及其项目媒体。',
    siliconBudgetUsage: '已用 {used} / {budget} tokens',
    siliconStart: '运行硅基预试',
    siliconNeedVlm: '使用你配置的硅基 VLM（必须能看图）。请在「AI 与集成」中设置。',
    siliconRuns: '运行记录',
    siliconCancel: '取消',
    siliconCompare: '对照',
    siliconCount: '合成答卷',
    siliconAnswers: '条答案',
    siliconPickPersona: '请至少选择一个人设。',
    siliconResume: '继续未完成任务',
    siliconRetryFailed: '仅重试失败项',
    siliconResumeHint: '保留已保存答案，只处理未完成的轮次。',
    siliconRetryHint: '只把本任务中失败或未知的轮次重新排队，不会新开一整批。',
    siliconReuseHint: '用相同设置创建新任务。旧任务的答案不会被覆盖。',
    siliconUnverifiedQs: '已列出但尚未预发布验收',
    siliconErrorLease: '当前数据库尚未应用硅基后台租约。',
    draftSaveRpcMissing: '当前数据库尚未应用草稿保存函数。请执行 save_project_draft_revision_id.sql。',
    assistantRunSchemaMissing: '当前数据库尚未应用助手 run 列。',
    siliconExport: '导出 JSON',
    siliconExportCsv: '导出 CSV',
    siliconDraftVersion: '草稿版本',
    siliconModelLabel: '模型',
    siliconSelectedPersonas: '已选人设',
    siliconSupportedQs: '硅基可以作答',
    siliconUnsupportedQs: '硅基将跳过',
    siliconNoAnswerable: '已保存草稿中没有可作答题。',
    siliconSelectQuestions: '开始前请至少选择一道受支持题目。',
    siliconSnapshotPages: '快照页数',
    siliconSnapshotMedia: '快照媒体',
    siliconCannotStart: '没有可测的受支持题目。未测项已列出，局部成功不能写成整卷通过。',
    siliconPartialPretest: '这是仅覆盖受支持题目的局部预试。未测项已列出，局部成功不能写成整卷通过。',
    siliconOpenPersona: '打开人设全文',
    siliconValidRate: '有效率',
    siliconFailureReason: '失败原因',
    siliconShownMedia: '本次展示媒体',
    siliconStatusAllFailed: '已结束，未获得有效答案',
    siliconStatusPartialValid: '已结束 — 部分答案无效或跳过',
    siliconProgressCounts: '已处理 {done}/{total} 轮 · 有效 {answered} · 跳过 {skipped} · 失败 {errors} · 剩余 {remaining}',
    siliconStatusAllComplete: '已结束，已获得有效答案',
    aiChangeCardTitle: '修改结果',
    aiChangeVerified: '已保存并核对',
    aiChangeSaved: '已保存',
    aiChangeUnsaved: '未保存',
    aiChangeSaveFailed: '保存失败',
    aiChangeVerifyIncomplete: '核对未完成',
    aiChangeNoDiff: '没有可展示的字段变化',
    aiWriteConflict: '编辑器有未保存修改。先保存再执行本条，或返回设置。',
    aiSaveThenRun: '保存后执行',
    aiReturnToEditor: '返回设置',
    aiQueueTitle: '任务队列',
    aiQueueAfter: '完成后执行',
    aiQueueNow: '补充当前',
    aiQueueCancel: '取消',
    siliconStatusPartial: '部分完成 — 预算中止',
    siliconStatusBudgetFailed: '已停止 — Token 预算耗尽',
    siliconStatusFailed: '失败',
    siliconStatusCancelled: '已取消',
    siliconStatusRunning: '进行中',
    siliconStatusLoading: '正在加载统计…',
    siliconTasksTitle: '硅基任务',
    siliconTasksTab: '运行任务 · {count}',
    siliconTasksBadge: '任务 · {count} 运行中',
    collectingSurveysBadge: '问卷 · {count} 收集中',
    collectingSurveysTitle: '你已发布且仍开放作答的问卷。项目上有明确收集标记时按标记计算；否则计入已发布且未标记关闭的问卷。草稿和他人的问卷不计入。',
    siliconTasksActive: '{count} 个进行中的硅基任务',
    siliconTasksRecent: '已结束',
    siliconTasksEmpty: '还没有硅基任务。',
    siliconTasksEmptyActive: '没有进行中的硅基任务。',
    siliconTasksEmptyEnded: '还没有已结束的硅基任务。',
    siliconTasksInProgress: '进行中（{count}）',
    siliconTasksEnded: '已结束（{count}）',
    siliconTasksBack: '返回',
    siliconProcessTitle: '运行过程',
    siliconProcessSamples: '已处理 {done}/{total} 轮',
    siliconProcessUnits: '已处理 {processed}/{total} 轮：有效 {valid}，跳过 {skipped}，失败 {failed}',
    siliconProcessCurrent: '当前：{persona} · 第 {repeat} 份答卷 · {question} · 第 {trial}/{trials} 轮',
    siliconProcessWaiting: '正在等待模型返回 · 本步已等待 {seconds} 秒',
    siliconProcessTimeline: '技术详情',
    siliconConfigSummary: '{personas} 个人设 · {envelopes} 份模拟答卷 · {questions} 道题，共 {trials} 轮',
    siliconProcessedTrials: '已处理 {done}/{total} 轮',
    siliconEndedNoAnswers: '已结束，未获得有效答案',
    siliconTrialBreakdown: '已处理 {done}/{total} 轮：有效 {valid}，跳过 {skipped}',
    siliconNoImagesReason: '原因：未解析到可用图片',
    siliconReuseSettings: '复用设置重新运行',
    siliconMediaPreviewLibrary: '本次使用平台预览媒体库 · 可用 {count} 张图片',
    siliconMediaProject: '本次使用项目媒体 · 可用 {count} 张图片',
    siliconMediaNone: '本次没有可用媒体来源',
    siliconEnvelopeLabel: '{persona} · 第 {repeat} 份答卷',
    siliconQuestionCard: '第 {n} 题｜{title}',
    siliconTrialProgress: '已处理 {done}/{total} 轮 · 正在进行第 {current} 轮',
    siliconWaitingStart: '等待开始',
    siliconTechDetails: '技术详情',
    siliconEndedAt: '结束于 {time}',
    siliconDuration: '运行 {duration}',
    siliconDurationSeconds: '{seconds} 秒',
    siliconDurationMinutes: '{minutes} 分 {seconds} 秒',
    siliconStatusQueued: '排队中',
    siliconStatusThrottled: '等待限流',
    siliconStatusStopping: '正在停止',
    siliconErrorNoMedia: '没有可用媒体来源。项目媒体为空，预览媒体库也没有图片。',
    siliconErrorFolderEmpty: '指定文件夹没有匹配图片。',
    siliconErrorImageUnreadable: '图片链接无法读取。',
    siliconErrorModel: '模型请求失败。',
    siliconViewProcess: '查看过程',
    siliconViewAnswers: '查看已完成答卷',
    siliconOpenProject: '打开所属项目',
    siliconStopping: '正在停止…',
    siliconConnectionLost: '连接中断。任务仍在服务端推进，这不是任务失败。',
    siliconTaskFinished: '{project} 的硅基任务已{status}。',
    siliconTaskCounts: '已处理 {processed}/{total} 轮 · 有效 {valid} · 失败 {failed}',
    siliconRationale: '作答依据',
    siliconEventStart: '已开始本题',
    siliconEventMedia: '已准备本轮图片',
    siliconEventRequest: '正在请求模型',
    siliconEventResponse: '已收到答案',
    siliconEventValid: '答案校验通过',
    siliconEventInvalid: '答案校验失败',
    siliconEventSkip: '已跳过',
    siliconEventSaved: '答案已保存',
    siliconEventRetry: '重试中',
    siliconEventThrottle: '排队或限流中',
    siliconEventStopping: '正在停止',
    siliconEventComplete: '任务完成',
    siliconEventFailed: '任务失败',
    siliconStageQueued: '排队中',
    siliconScienceNote: '这些数字只是合成预试，不能当作问卷已科学有效的证据。',
    featureAssistant: '浏览器内助手',
    featureAssistantHint: '与硅基预试独立。关闭后隐藏助手侧栏。',
    featureSilicon: '硅基预试（实验）',
    featureSiliconHint: '与助手独立。未达标前保持实验可见范围。',
    qualityChecks: '运行设计检查',
    qualityChecksEmpty: '未发现可立即处理的设计问题。',
    qualityCheckResearch: '研究设计',
    qualityCheckFlow: '参与者流程',
    qualityCheckExport: '分析 / 导出',
    resultsSiliconHint: '默认只看真人结果。硅基对照请打开「硅基样本」标签。',

    navDocs: 'SP-Wiki',
    navPaperLibrary: '论文库',
    navNews: '新闻',
    navForum: '论坛',
    navRequestTemplate: '申请模板',
    navRequestDesign: '申请设计',
    navTeam: '团队',
    navLiveSurveys: '在线调查',
    navSpBench: 'SP-Bench',
    navResearcherLogin: '研究者登录',
    navOpenWorkspace: '进入工作区',
    footerDevelopedBy: '开发团队',
    footerCite: '引用与论文',

    newsTitle: '新闻',
    newsSubtitle: 'SP-Survey 的产品更新、发布说明与研究动态。',
    newsEmpty: '暂无内容，稍后再来看看。',
    newsBackToList: '全部新闻',
    newsNotFound: '未找到该文章，或尚未发布。',
    newsNoBody: '暂无正文。',
    newsUnavailable: '新闻功能尚未就绪。',

    benchNotOpenTitle: 'SP-Bench',
    benchNotOpenBody: '公开评测榜暂未开放。',
    benchMethod: '方法',
    benchDataset: '数据集',
    benchSamples: '样本',
    benchModels: '模型',
    benchOverall: '总榜',
    benchObjective: '客观感知',
    benchSubjective: '主观感知',
    benchCognition: '空间认知',
    benchMethodTab: '方法说明',
    benchLandingFallback: '面向城市环境主观—客观空间感知与认知的多模态模型基准评测。',
    benchNoResultsYet: '榜单即将发布',
    benchViewLeaderboard: '查看排行榜',

    feedbackButton: '提建议',
    feedbackTitle: '给 SP-Survey 提建议',
    feedbackAnonymousHint: '不登录也可以提。登录后提交，我们能看到是哪个账号提的。',
    feedbackSignedInAs: '将以 {email} 的身份提交。',
    feedbackCategory: '类型',
    feedbackCatSuggestion: '建议',
    feedbackCatFeature: '想要的功能',
    feedbackCatBug: '问题 / Bug',
    feedbackCatOther: '其他',
    feedbackMessage: '你的建议',
    feedbackMessagePlaceholder: '哪里可以做得更好？想要什么功能？遇到了什么问题？',
    feedbackContactTitle: '联系方式（选填，方便我们回复你）',
    feedbackContactName: '称呼',
    feedbackContactEmail: '邮箱',
    feedbackContactWechat: '微信号',
    feedbackContactOther: '其他联系方式',
    feedbackContactOtherPlaceholder: '电话、QQ、小红书…',
    feedbackSubmit: '提交',
    feedbackSending: '提交中…',
    feedbackThanks: '谢谢！你的建议已经发给 SP-Survey 团队。',
    feedbackSendAnother: '再提一条',
    feedbackRateLimited: '刚才提交得有点多，请稍等一会儿再试。',
    feedbackUnavailable: '建议功能暂时不可用，请稍后再试。',
    feedbackFailed: '提交失败，请检查网络后重试。',
    landGrowthTitle: '平台增长',
    landGrowthSubtitle: '平台实时累计数据，以及最近 12 个月的增长。仅平台管理员可见。',
    landGrowthUnavailable: '暂时读不到增长数据。请确认已在 Supabase 运行 supabase/site_growth_stats.sql，并且当前账号是平台管理员。',
    landGrowthUsers: '注册研究者',
    landGrowthProjects: '问卷项目',
    landGrowthResponses: '已收集回答',
    landGrowthVisits: '近 30 天访问',
    landGrowthTotalHint: '累计',
    landGrowthVisitorsHint: '独立访客 {n}（按天计）',
    landLiveBannerOne: '当前有 {n} 份调查正在进行 — 可作为参与者填写。',
    landLiveBannerMany: '当前有 {n} 份调查正在进行 — 可作为参与者填写。',
    landBrowseLive: '浏览在线调查',
    landHeroTitle: '街景感知调查',
    landHeroLine1: '面向城市街景视觉感知研究的专业级调查平台。',
    landHeroLine2: '构建、分享并分析街景感知研究 — 无需编程。',
    landStartFree: '免费开始 →',
    landReadPaper: '阅读论文',
    landFeaturesTitle: '感知研究所需的一切',
    landFeatUploadTitle: '上传图片',
    landFeatUploadDesc: '将街景图片直接上传到云存储，自动压缩以保持问卷加载迅速。',
    landFeatAiTitle: 'AI 问卷构建',
    landFeatAiDesc: '描述研究目标，由 ChatGPT 生成完整问卷，并使用经验证的题型。',
    landFeatShareTitle: '即时分享',
    landFeatShareDesc: '立即获得可分享链接 — 无需服务器、无需部署、无需复杂配置。',
    landFeatResultsTitle: '结果分析',
    landFeatResultsDesc: '按题目查看答卷，支持图片与回答配对，并可导出 CSV。',
    landTemplatesTitle: '模板库',
    landSchemeTitle: '用 SP-Scheme 让 AI 设计的问卷',
    landSchemeSubtitle: '这些问卷页面由大模型按 SP-Scheme 规范设计，再整份上传到平台。参与者看到的就是这个页面，平台负责记录和分析回答。可以先预览，也可以直接拿来做自己的问卷。',
    landTemplatesSubtitle: '从已发表研究中使用的同行评议问卷设计起步',
    landRequestTemplate: '为你的论文申请模板',
    landRequestDesign: '申请问卷设计协助',
    landSearchTemplates: '按名称、作者或主题搜索模板…',
    landCatAcademic: '学术研究',
    landCatUrban: '城市理论',
    landCatAi: 'AI 生成',
    landSearchCategoryHint: '搜索以浏览该类别全部 {n} 个模板。',
    landNoTemplates: '未找到与 “{q}” 匹配的模板',
    landCtaTitle: '准备好开始你的调查了吗？',
    landCtaBody: '创建账号，10 分钟内即可上线第一份问卷。',
    landGetStarted: '免费开始',
    landPreview: '预览',
    landUse: '使用',
    landPaper: '论文',
    landDataset: '数据集',
    landChipAi: 'AI',
    landChipUrban: '城市',
    landChipAcademic: '学术',
    landPreviewTitle: '预览 — {name}',
    landReadOnly: '（只读）',
    landClose: '关闭',

    trialDoNTimes: '本题共需完成 {n} 轮。当前第 {current} / {n} 轮。',
    trialProgressCount: '已完成 {done} / {n}，剩余 {left} 轮。',
    trialAutoAdvance: '作答后会自动进入下一轮。',
    trialAutoAdvanceAllocation: '每个选项都设好，或点数用完后，会自动进入下一轮。若想留有剩余点数，请点「下一轮」。',
    trialUseNext: '答完后请点「下一轮」。',
    trialLastRound: '最后一轮 — 完成后即可继续问卷。',
    trialAllDone: '已完成全部 {n} 轮。',
    trialContinue: '继续下一题',
    trialFinishSurvey: '完成问卷',
    trialBack: '返回',
    trialNextRound: '下一轮',
    trialPageNextBlocked: '请先完成全部轮次再继续。',
    progressRound: '第 {current} / {n} 轮',
    trialLabel: '第 {n} 轮',
    trialReviewAnswers: '检查答案',
    progressPage: '第 {current} / {n} 页',
    progressQuestion: '第 {current} / {n} 题',
    progressQuestionLabel: '第 {n} 题',
    progressTrialCount: '已完成 {done}/{n} 轮',
    progressNotReached: '尚未到达',
    progressJump: '{label} — 点击跳转',
    progressPreviousGroup: '上一组题目',
    progressNextGroup: '下一组题目',
    participantSaving: '正在保存您的答案…',
    participantThanks: '感谢参与！',
    participantCompleted: '感谢您完成问卷！',
    participantRoundsCompleted: '已完成全部 {n} 轮标注！',
    participantCompletionCode: '完成码：',
    participantIdLabel: '您的参与者编号：',
    participantSavedLocally: '答案已保存在本地。',
    participantSaved: '答案已成功保存。',
    participantSaveError: '由于网络或服务器问题，答案暂时未能保存。您的答案已保留，请重试提交。',
    participantRetrySubmission: '重试提交',
    participantResumeTitle: '继续上次的问卷？',
    participantResumeBody: '发现您在 {date} 保存的未完成问卷。是否从上次的位置继续？',
    participantPreviousVisit: '上次访问时',
    participantStartFresh: '重新开始',
    participantResume: '继续填写',

    loginBrand: '街景感知调查',
    loginSubtitle: '研究者登录',
    loginSignIn: '登录',
    loginCreateAccount: '创建账号',
    loginEmail: '邮箱',
    loginPassword: '密码',
    loginPasswordHelp: '至少 6 个字符',
    loginSigningIn: '登录中…',
    loginCreating: '创建账号中…',
    loginRegisterSuccess: '注册成功！请查收邮件完成确认后再登录。',
    loginErrorGeneric: '发生错误',
    loginBackHome: '← 返回首页',
    loginForgot: '忘记密码？',
    loginResend: '重发确认邮件',
    loginEnterEmailFirst: '请先在上面填写邮箱。',
    loginResendSent: '确认邮件已重新发送，请查看收件箱和垃圾邮件。',
    loginForgotTitle: '重置密码',
    loginForgotHelp: '输入注册时用的邮箱，我们会发一封设置新密码的邮件。',
    loginSendResetLink: '发送重置邮件',
    loginResetSent: '如果这个邮箱已注册，重置邮件已经发出。请查看收件箱和垃圾邮件。',
    loginResetLinkInvalid: '重置链接无效或已过期，请在下面重新发送。',
    loginBackToSignIn: '返回登录',
    loginNewPasswordTitle: '设置新密码',
    loginNewPassword: '新密码',
    loginSaveNewPassword: '保存新密码',
    notFoundTitle: '页面不存在',
    notFoundBody: 'SP-Survey 上没有这个地址。如果是别人发给你的问卷链接，请确认链接复制完整。',
    notFoundHome: '回到首页',

    liveTitle: '在线调查',
    liveBody: '平台当前展示的社区研究。参与者链接遵循研究者设置的正式版本。灰色卡片表示不在批准的时间窗口内。',
    liveEmpty: '暂无展示中的在线调查。研究者可在项目菜单中申请「发布到主页」；管理员在后台审核后展示。',
    liveNowOnline: '正在进行（{n}）',
    liveUpcomingClosed: '即将开始与已结束',
    liveOnline: '进行中',
    liveUpcoming: '即将开始',
    liveClosed: '已结束',
    liveNoDescription: '暂无描述。',
    liveTakeSurvey: '填写问卷',
    liveNotOpenYet: '尚未开放',

    teamTitle: '团队',
    teamIntroBefore: 'SP-Survey 由',
    teamIntroMid: '在',
    teamIntroAfter: '（新加坡国立大学）主导，与合作机构共同推进，并由 Filip Biljecki 指导。',
    teamCollaborators: '合作者',
    teamAffiliations: '单位',
    teamProfile: '主页',
    teamProjectLead: '项目负责人',
    teamPI: '首席研究员',
    teamLeadBlurb: '发起并主导 SP-Survey 的设计、开发与开源发布——从平台架构、问卷工具到托管研究流程。',
    teamPiBlurb: 'Urban Analytics Lab 创始人；为 SP-Survey 提供学术指导与研究方向。',
    teamRolePhd: '博士研究生',
    teamRoleEngineer: '研究工程师',
    teamRoleFellow: '研究员',
    teamRoleVisiting: '访问学者',
    teamRoleAssocProf: '副教授',

    papersTitle: '论文库',
    papersCount: '{n} 篇论文',
    papersWithTemplate: '{n} 篇有模板',
    papersWithTemplateTitle: '仅显示已关联问卷模板的论文',
    papersBody: '精选的城市影像 × 人工调查文献，用于支撑本平台的问卷模板。管理员将论文匹配到现有模板后，会显示关联模板 ID。',
    papersAll: '全部论文',
    papersHasTemplate: '有模板（{n}）',
    papersNoTemplate: '无模板（{n}）',
    papersSearchPlaceholder: '标题 / DOI / 期刊 / 作者 / 模板 ID',
    papersSearch: '搜索',
    papersClearSearch: '清除搜索',
    papersShowing: '显示 {shown} / {total} 篇',
    papersHasTemplateSuffix: ' · 有模板',
    papersNoTemplateSuffix: ' · 无模板',
    papersNoMatch: '没有匹配的论文。',
    papersOpen: '打开论文',
    papersColTitle: '标题',
    papersColYear: '年份',
    papersColTemplate: '模板',
    papersColLink: '链接',

    reqCopied: '已复制',
    reqCopy: '复制',
    reqSubmitAnother: '再提交一份',
    reqBackHome: '返回首页',
    reqStarting: '开始中…',
    reqUploading: '上传中（{current}/{total}）…',
    reqSubmitting: '提交中…',
    reqSubmit: '提交申请',
    reqChooseFiles: '选择文件',
    reqClear: '清除',
    reqFilesSelected: '已选 {n} 个文件',
    reqMore: '另有 {n} 个',
    reqMax: '最多 {n}',
    reqUploaded: ' 已上传。',
    reqFileTooLarge: '“{name}”过大（最大 {mb} MB）。',
    reqUploadUnavailable: '当前无法上传文件。请先不带文件提交，或稍后再试。',

    reqTplTitle: '为你的论文申请模板',
    reqTplIntro: '无需账号。提供论文信息、可选的调查数据集图片与补充文件（PDF 等）。我们会生成待审核的模板 ID，审核通过后即可使用。需要协助设计定制研究？',
    reqTplLinkDesign: '申请问卷设计',
    reqTplSuccess: '申请已提交。请保存你的待审核模板 ID。',
    reqTplId: '模板 ID',
    reqTplImagesCount: '{n} 张调查数据集图片',
    reqTplSuppCount: '{n} 个补充文件',
    reqTplStatus: '状态：待审核（尚未进入公共库）。通过后，打开研究者面板并使用此模板 ID。',
    reqTplNeedFields: '请填写论文标题、作者与论文链接。',
    reqTplBadYear: '年份须为四位数字（如 2025）。',
    reqTplBadEmail: '请输入有效邮箱，或留空。',
    reqTplPaperTitle: '论文标题',
    reqTplAuthors: '作者',
    reqTplAuthorsHelp: '例如 Yang, Sijie and Chong, Adrian and Liu, Pengyuan and Biljecki, Filip',
    reqTplYear: '年份',
    reqTplYearPh: '如 2025',
    reqTplLink: '论文链接',
    reqTplLinkPh: 'https://doi.org/… 或出版商链接',
    reqTplEmail: '邮箱（可选）',
    reqTplEmailHelp: '可选 — 便于我们就该申请联系你。无需账号。',
    reqTplNotes: '备注（可选）',
    reqTplNotesHelp: '方法细节、问卷设计提示、Hugging Face 数据集等。',
    reqTplImages: '调查数据集图片（可选）',
    reqTplImagesHelp: '上传前会自动压缩图片。',
    reqTplChooseImages: '选择图片',
    reqTplSupp: '补充文件（可选）',
    reqTplSuppHelp: 'PDF、Word、幻灯片、表格、ZIP 等 — 每个最大 {mb} MB。',

    reqDesTitle: '申请问卷设计',
    reqDesIntro: '计划做一项感知研究，希望协助做成 SP-Survey？请提交简短研究说明 — 这是尽力而为的研究协作，并非付费服务。若要把已发表论文做成可复用模板？',
    reqDesLinkTpl: '改为申请模板',
    reqDesSuccess: '申请已提交。请保存申请 ID — 我们有能力协助时会通过邮件联系你。',
    reqDesId: '申请 ID',
    reqDesMediaCount: '{n} 个媒体文件',
    reqDesSuppCount: '{n} 个补充文件',
    reqDesStatus: '状态：待处理。我们按容量审核申请，若能协助设计或搭建问卷会发邮件通知。',
    reqDesStartSelf: '或自己开始构建',
    reqDesNeedFields: '请填写姓名、邮箱、研究标题与研究说明。',
    reqDesBadEmail: '请输入有效邮箱，以便我们跟进。',
    reqDesBriefShort: '请更详细描述你的研究（至少几句话）。',
    reqDesName: '你的姓名',
    reqDesEmail: '邮箱',
    reqDesEmailHelp: '必填，便于我们跟进。',
    reqDesAffiliation: '单位（可选）',
    reqDesAffiliationPh: '如 Urban Analytics Lab, NUS',
    reqDesStudyTitle: '研究标题',
    reqDesBrief: '研究说明',
    reqDesBriefHelp: '你想测量什么、对象是谁、大致如何进行？几句话即可。',
    reqDesStimulus: '刺激材料（可选）',
    reqDesStimulusHelp: '参与者将看到或听到的内容。',
    reqDesStimImage: '图片',
    reqDesStimVideo: '视频',
    reqDesStimAudio: '音频',
    reqDesStimMixed: '混合媒体',
    reqDesStimOther: '其他 / 尚未确定',
    reqDesTimeline: '时间安排（可选）',
    reqDesTimelinePh: '如 8 月预实验，10 月正式收集',
    reqDesRelated: '相关链接（可选）',
    reqDesRelatedPh: '论文、项目页或数据集 URL',
    reqDesNotes: '补充说明（可选）',
    reqDesNotesHelp: '设想的题型、样本量、约束条件等。',
    reqDesSampleMedia: '样例媒体（可选）',
    reqDesSampleHelp: '图片、视频或音频示例。图片上传前会自动压缩。',
    reqDesChooseMedia: '选择媒体',
    reqDesSupp: '补充文件（可选）',
    reqDesSuppHelp: '方案说明、PDF、幻灯片、表格、ZIP 等 — 每个最大 {mb} MB。',
    reqDesInfo: 'SP-Survey 团队在能力范围内尽力支持。你也可以随时注册账号自行搭建问卷。',

    regionLabel: '区域',
    globalMode: '全球',
    chinaMode: '中国',
    languageToggle: 'English',
    imageDataset: '媒体数据集',
    datasetProvider: '数据集平台',
    huggingface: 'Hugging Face',
    modelscope: 'ModelScope（魔搭）',
    msToken: 'ModelScope 访问令牌（可选）',
    hfDatasetName: 'HuggingFace 数据集名称',
    msDatasetName: 'ModelScope 数据集名称',
    msPlaceholder: '如 sijiey/Thermal-Affordance-Dataset',
    hfTokenHelp: '可选：仅私有数据集需要。前往 huggingface.co/settings/tokens 获取',
    msTokenHelp: '可选：仅私有数据集需要。前往 modelscope.cn/my/myaccesstoken 获取',
    hfBrowse: '浏览 HuggingFace 数据集',
    msBrowse: '浏览 ModelScope 数据集',
    deploymentGuide: '部署指引',
    globalDeployment: 'Vercel 部署',
    chinaDeployment: 'Zeabur 部署（中国可用）',
    storageProvider: '图片存储平台',
    supabase: 'Supabase 存储',
    aliyunOss: '阿里云 OSS',
    chinaModeBanner: '中国区模式已启用 — 使用 ModelScope、阿里云 OSS 与 Zeabur',

    tplExportAll: '导出全部线上模板',
    tplExportAllTooltip: '把 Supabase 中的全部模板下载为内置 ZIP（JSON + index.json，不含图片），解压到 public/project_templates/ 后提交。_export/manifest.json 记录导出时间和每个模板的 updated_at。',
    tplExportAllDone: '已导出 {count} 个线上模板为内置 ZIP（解压到 public/project_templates/ 后提交）',
    tplExportAllSkipped: '跳过 {count} 个缺少名称或配置的模板：{ids}',
    tplExportAllLog: '已导出全部线上模板 → 仓库 ZIP（{count}）：{files}',
    tplSeedSelectAll: '全选',
    tplSeedConflict: '导出后线上已修改',
    tplSeedConflictDetail: '线上更新于 {online}，导出快照为 {exported}。默认不勾选；勾选后会用内置版本覆盖更新的线上版本。',
    tplSeedConflictSummary: '{count} 个模板在导出（{exportedAt}）后线上又被修改，默认不勾选；如需覆盖请单独勾选。',
    tplSeedConflictChip: '冲突 {count}',
    tplSeedPending: '待审核',
    tplSeedHidden: '不在首页展示',
    tplSeedKeepsStatus: '已存在的模板保留线上的审核状态、首页展示和提交者；新模板沿用导出时的状态（待审核的仍为待审核）。',
  },
};

/** Replace `{n}` / `{name}` placeholders in a template string. */
export function tf(template, vars = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : `{${key}}`
  );
}
